import { createSlice } from '@reduxjs/toolkit';
import { constants } from '../../shared/enums/constants';
import { RequestStatus } from '../../shared/enums/request-status';
import { ResponseErrorWithHandled } from '../../shared/types';
import { AddProspectResponse } from '../sequence/types';
import { Field } from '../settings/types/field';
import {
  getProspectRequest,
  getProspectCountRequest,
  getSequenceListRequest,
  getStepListRequest,
  getStepDetailsListRequest,
  addToStepRequest,
  removeProspectRequest,
  unsubscribeProspectRequest,
  getSingleProspectDetailsRequest,
  getSingleProspectSequencesDetailsRequest,
  singleProspectSequencesMarkAsRepliedRequest,
  singleProspectRemoveFromSequencesRequest,
  saveContactAttributesRequest,
  saveContactDealValueRequest,
  getFieldsRequest,
  addProspectManuallyRequest,
  singleProspectSequencesMarkAsFinishedRequest,
  verifyProspectsManuallyRequest,
  getTagsRequest,
  addTagsToProspectsRequest,
  removeTagsFromProspectsRequest,
  verifyBulkProspectsRequest,
  deleteBulkProspectsRequest,
  unsubscribeBulkProspectRequest,
  addBulkProspectsToStepRequest,
  addTagsToBulkProspectsRequest,
  removeTagsFromBulkProspectsRequest,
  changePrimaryEmailInProspectRequest,
  deleteSecondaryEmailRequest,
  deleteSecondaryPhoneRequest,
  getProspectListRequest,
  getProspectListCountRequest,
  getProspectListByIdsRequest,
  getFilterViewListRequest,
  pinUnpinSavedFilterRequest,
  updateSavedFilterRequest,
  deleteSavedViewRequest,
  getProspectFiltersFieldsListRequest,
  getProspectFiltersFieldValuesRequest,
  getProspectLeadFinderListsRequest,
  getProspectFiltersAttributeFieldValuesRequest,
  getProspectFiltersSequencesListRequest,
  getProspectFiltersClientsListRequest,
  getProspectFiltersTagsListRequest,
  getProspectFiltersOwnersListRequest,
  getProspectFiltersOwnersListForAgencyRequest,
  getProspectFiltersOutcomeListRequest,
  getProspectFiltersSubsequenceListRequest,
  saveProspectFiltersViewRequest,
  updateProspectFiltersViewRequest,
  deleteProspectFiltersViewRequest,
  getContactListByViewIdRequest,
  getImportedFilesListRequest,
  updateProspectFieldByIdRequest,
  getLongTextByProspectAndColumnIdRequest,
  getTemplateOptionList,
  getAiModelRequest,
  getTemplateByIdRequest,
  createAiColumnTemplateRequest,
  updateAiColumnTemplateRequest,
  deleteAiColumnTemplateRequest,
  saveAndDontRun,
  getAiColumnPreviewRequest,
  getTemplateConfigResponse,
  regenerateAiColumnCellRequest,
  aiColPlayPauseEnrichmentRequest,
  aiColActiveFieldsEnrichmentStatus,
  updatePrimaryNumberRequest,
  getEligibilityCheckRequest,
  getKanbanListRequest,
  updateKanbanViewRequest,
  createCustomFieldRequest,
  getProspectNotesRequest,
  createProspectNotesListRequest,
  deleteProspectNoteRequest,
  prospectNotePinRequest,
  updateProspectNoteRequest,
  updateProspectNoteVisibilityRequest,
  getSinglePropsectDetailPinnedFieldsRequest,
  updateProspectPinStatusRequest,
  getMinimalEmailAccountListRequest,
  getProspectActivityCountRequest,
} from './extra-actions';
import { getTagsToLeadsRequest } from '../leads/extra-actions';
import { onOutcomeChangeRequest } from '../sequence/extra-actions';
import { AiColEnrichmentModalSteps } from './components/ai-col-enrichment-modal/enums';
import {
  getProspectsFiltersFromLocalStore,
  removeProspectsFiltersFromLocalStore,
  setProspectsFiltersInLocalStore,
} from './helpers/prospect-filters-resolver';
import {
  aiColActiveFieldsStatusProps,
  EnrichmentTemplateList,
  Filters,
  getAiModelsProps,
  getTemplateByIdProps,
  getTemplateConfigProps,
  ProspectPagination,
  SavedFilter,
  saveAndDontRunPayload,
  // saveRegeneratedTemplateProps,
  SelectAllContacts,
  KanbanProspectListPayload,
  GetProspectNotesList,
  NoteItem,
  getSingleProspectPinnedFields,
  EmailAccountMinimalListResponse,
  PinUnpinActionType,
  FilterGroupLogicPayload,
} from './types';
import {
  ProspectsFilters,
  ProspectFilterResetArg,
} from '../../shared/types/prospects-filters';
import { getMergedProspects } from './helpers/utils/get-merged-prospects';
import { ProspectSortBy, ViewType } from './enums/prospect';
import { SortOrder } from '../../shared/enums/order';
import {
  FilterGroup,
  ProspectFilterField,
  ProspectFiltersFieldsAttributesValue,
} from './components/prospect-filters-modal/types';
import { getSortedProspectFilterFields } from './components/prospect-filters-modal/utils/helpers';
import { setProspectTableColumnsInLocalStorage } from './components/prospect-table/utils/helper';
import { getDefaultFilterGroupObject } from './components/prospect-filters-modal/utils/get-default-filter-group-object';
import { parseSortingFromResponse } from './helpers/helper';

interface RequestState {
  status: RequestStatus;
  message: string;
  error: ResponseErrorWithHandled;
}

export const prospectsFiltersInitialState: ProspectsFilters = {
  conversationType: [],
  tags: [],
  owners: [],
  status: [],
  outcomes: [],
  sequences: [],
  'verification status': [],
  'task priority': [],
  'created date': [],
  'creation source': [],
  'current step': [],
  'email account': [],
  'client associated': [],
  'imported file': [],
  subSequenceIds: [],
  subSequenceStepIds: [],
  callOutcomes: [],
};

interface State {
  getProspectRequest: RequestState;
  getSequenceListRequest: RequestState;
  getStepListRequest: RequestState;
  getStepDetailsListRequest: RequestState;
  getProspectCountRequest: RequestState;
  addToStepRequest: RequestState;
  addToStepRequestResultCount: {
    failed: number;
    successful: number;
    resultData: any;
  };
  removeProspectRequest: RequestState;
  unsubscribeProspectRequest: RequestState;
  getSingleProspectDetailsRequest: RequestState;
  getSingleProspectSequencesDetailsRequest: RequestState;
  singleProspectSequencesMarkAsRepliedRequest: RequestState;
  singleProspectSequencesMarkAsFinishedRequest: RequestState;
  singleProspectRemoveFromSequencesRequest: RequestState;
  saveContactAttributesRequest: RequestState;
  saveContactDealValueRequest: RequestState;
  getFieldsRequest: RequestState;
  addProspectManuallyRequest: RequestState;
  verifyProspectManuallyRequest: RequestState;
  getTagsRequest: RequestState;
  getTagsToLeadsRequest: RequestState;
  addTagsToProspectsRequest: RequestState;
  removeTagsFromProspectsRequest: RequestState;
  verifyBulkProspectsRequest: RequestState;
  deleteBulkProspectsRequest: RequestState;
  unsubscribeBulkProspectRequest: RequestState;
  addBulkProspectsToStepRequest: RequestState;
  addTagsToBulkProspectsRequest: RequestState;
  removeTagsFromBulkProspectsRequest: RequestState;
  changePrimaryEmailInProspectRequest: RequestState;
  deleteSecondaryEmailRequest: RequestState;
  deleteSecondaryPhoneRequest: RequestState;
  getFilterViewListRequest: RequestState;
  pinUnpinSavedFilterRequest: RequestState;
  updateSavedFilterRequest: RequestState;
  deleteSavedViewRequest: RequestState;
  getContactListByViewIdRequest: RequestState;
  getImportedFilesListRequest: RequestState;
  updatePrimaryNumberRequest: RequestState;
  search: string;
  steps: any[];
  sequences: any[];
  selectedContacts: any[];
  selectAllContactDetails: SelectAllContacts;
  singleProspect: any;
  getSingleProspect: any;
  singleProspectSequenceDetails: any[];
  tags: any[];
  leadTags: any[];
  isProspectSubscribed: boolean;
  prospectPaginationData: ProspectPagination;
  addProspectResponse: AddProspectResponse;
  prospectsFilters: ProspectsFilters;
  getKanbanListRequest: RequestState;
  updateKanbanViewRequest: RequestState;

  // New State
  prospects: any[];
  prospectCount: number;
  prospectKanbanData: Record<string, any[]>; // Store kanban data with dynamic column names as keys
  prospectKanbanCount: Record<string, number>; // Store kanban column counts with column names as keys
  prospectKanbanContactListViewData: Record<string, any>;
  prospectKanbanViewData: Record<string, any> | null; // Store Kanban data fetched via saved views
  filters: Filters;
  fields: Field[];
  savedFilters: SavedFilter[];
  selectedSavedView: SavedFilter;
  prospectFiltersFieldsList: ProspectFilterField[];
  prospectFiltersFieldsAttributesValue: ProspectFiltersFieldsAttributesValue;
  prospectFiltersFieldsAttributesLoading: Record<string, boolean>;
  prospectFiltersFieldsAttributesFetched: Record<string, boolean>;

  getProspectListRequest: RequestState;
  getProspectListCountRequest: RequestState;
  getProspectListByIdsRequest: RequestState;
  getProspectFiltersFieldsListRequest: RequestState;
  getProspectFiltersFieldValuesRequest: RequestState;
  getProspectFiltersAttributeFieldValuesRequest: RequestState;
  getProspectFiltersSequencesListRequest: RequestState;
  getProspectFiltersClientsListRequest: RequestState;
  getProspectFiltersTagsListRequest: RequestState;
  getProspectFiltersOwnersListRequest: RequestState;
  getProspectFiltersOwnersListForAgencyRequest: RequestState;
  getProspectFiltersOutcomeListRequest: RequestState;
  getProspectFiltersSubsequenceListRequest: RequestState;
  saveProspectFiltersViewRequest: RequestState;
  updateProspectFiltersViewRequest: RequestState;
  deleteProspectFiltersViewRequest: RequestState;
  updateProspectFieldByIdRequest: RequestState;
  getLongTextByProspectAndColumnIdRequest: RequestState;
  getAiModelRequest: RequestState;
  getAiModelTemplates: RequestState;
  getTemplateByIdRequest: RequestState;
  createAiColumnTemplateRequest: RequestState;
  updateAiColumnTemplateRequest: RequestState;
  deleteAiColumnTemplateRequest: RequestState;
  getAiColumnPreviewRequest: RequestState;
  saveAndDontRun: RequestState;
  getTemplateConfigResponse: RequestState;
  regenerateAiColumnCellRequest: RequestState;
  aiColPlayPauseEnrichmentRequest: RequestState;
  getEligibilityCheckRequest: RequestState;
  eligibilityCheckResponse: boolean | null;
  createCustomFieldRequest: RequestState;
  getProspectNotesRequest: RequestState;
  createProspectNotesListRequest: RequestState;
  deleteProspectNoteRequest: RequestState;
  updateProspectNoteRequest: RequestState;
  prospectNotePinRequest: RequestState;
  updateProspectNoteVisibilityRequest: RequestState;
  getSinglePropsectDetailPinnedFieldsRequest: RequestState;
  updateProspectPinStatusRequest: RequestState;
  getMinimalEmailAccountListRequest: RequestState;
  getProspectActivityCountRequest: RequestState;
  prospectActivityCount: {
    notes: string | number;
    tasks: string | number;
    emails: string | number;
    sequences: string | number;
  };
  aiColumnTemplatePreview: { preview: string };
  stepsDetails: {
    id: number;
    number: number;
  }[];
  importedFilesList: { name: string; value: number }[] | [];
  longText: string;
  aiTemplates: EnrichmentTemplateList[];
  aiTemplatesPagination: {
    currentPage: number;
    totalItems: number;
    hasMore: boolean;
  };
  selectedAiTemplateId: number;
  selectedAiTemplateShareableId: string;
  aiModelList: getAiModelsProps[];
  getTemplateById: getTemplateByIdProps;
  savedNewColumn: any;
  selectedProspectRowId: number[];
  deSelectedProspectRowId: number[];
  aiColumnTemplateRegenerate: getTemplateConfigProps;
  regeneratedAiColCellResponse: {
    prospectId: number;
    attribute: {
      id: number;
      fieldRefId: number;
      attributeValue: string;
    };
  };
  isAiColumnLoading: boolean;
  // Store the payload data for the current AI column loading operation
  currentAiColumnPayload: saveAndDontRunPayload | null;
  // Track cells currently regenerating to show per-cell loaders while Pending
  aiColumnloadingCell: {
    prospectId: number;
    fieldId: number;
  }[];
  inProgressTableCells: {
    fieldIds: string[];
    prospectIds: string[];
  };
  isBulkProspectSelected: boolean;
  aiColumnModalVisible: boolean;
  // Context to open AI Column Enrichment modal from outside without URL params
  aiEnrichmentOpenContext?: {
    step?: AiColEnrichmentModalSteps;
    shareableId?: string;
    fieldId?: string;
    fromOutside?: boolean;
  } | null;
  aiColActiveFieldsEnrichmentStatus: RequestState;
  aiColEnrichedField: aiColActiveFieldsStatusProps;
  aiColPlayPauseEnrichmentPayload: { message: string };
  visibleFieldIds: string[];
  aiColumnStoppedFieldIds: number;
  showAiColumnWelcomeScreen: boolean;
  isAiColumnTemplateListCachedFromFilter: boolean; // From Search or Category Change
  updatePrimaryNumberResponse: string;
  autoRunConditionFilterGroups: any;
  autoRunConditionFilterGroupsLogic: any;
  appliedProspectEnrichmentFilters: any;

  isAiColEnrichmentFromModal: boolean;
  AiColEnrichmentjobIdsInProgress: number[];
  enrichmentCompletedProspectIdsFromFirebase: number[];
  visibleProspectIdsInProspectTable: number[];
  // Contact IDs And Field IDs To Refresh
  contactIdsAndFieldIdsToRefresh: {
    contactIds: string[];
    fieldIds: string[];
  };
  createdKanbanViewId: number | null;
  kanbanViewList: KanbanProspectListPayload[];
  selectedKanbanCategoryId: number | null;
  selectedKanbanViewId: number | null;
  hasAppliedKanbanSelectedFields: boolean;

  // Kanban Related States
  activeViewId: number | null;
  currentViewType: ViewType | null; // Tracks whether current view is List or Kanban
  prospectNotesList: GetProspectNotesList;
  createProspectNoteResponse: NoteItem | null;
  updateProspectNoteResponse: NoteItem | null;
  updateProspectNoteVisibilityResponse: NoteItem | null;
  selectedKanbanCardsFields: any[];
  getSingleProspectPinnedFields: getSingleProspectPinnedFields;
  getEmailAccountList: EmailAccountMinimalListResponse;

  updatedCategoryResponse: any; // This will store new edited Category payload to pass in API
  prospectToNavigateFromModal: {
    ids: number[];
    columnName: string;
  };
  selectedCategoryName: KanbanProspectListPayload;
  showProspectDetailModal: boolean;
  shouldFetchInitialList: boolean;

  addClickedFieldId: string | null;
  isEmailFieldHovered: boolean;
  prospectViewIdFilter: FilterGroupLogicPayload;
  loadedSkeletonForCurrrentProspect: {
    sequences: boolean;
    tasks: boolean;
    emails: boolean;
  };
  addNewCategoryTriggered: boolean;
}

const initialState: State = {
  getProspectListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectListCountRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectListByIdsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectFiltersFieldsListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectFiltersFieldValuesRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectFiltersAttributeFieldValuesRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectFiltersSequencesListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectFiltersClientsListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectFiltersTagsListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectFiltersOwnersListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectFiltersOwnersListForAgencyRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectFiltersOutcomeListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectFiltersSubsequenceListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  saveProspectFiltersViewRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateProspectFiltersViewRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteProspectFiltersViewRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },

  getProspectRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSequenceListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getStepListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getStepDetailsListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectCountRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSingleProspectDetailsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSingleProspectSequencesDetailsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  addToStepRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  singleProspectSequencesMarkAsRepliedRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  singleProspectSequencesMarkAsFinishedRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  singleProspectRemoveFromSequencesRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  unsubscribeProspectRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  removeProspectRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  saveContactAttributesRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  saveContactDealValueRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getFieldsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  addProspectManuallyRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },

  verifyProspectManuallyRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTagsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTagsToLeadsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  addTagsToProspectsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  removeTagsFromProspectsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  verifyBulkProspectsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteBulkProspectsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  unsubscribeBulkProspectRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  addBulkProspectsToStepRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  addTagsToBulkProspectsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  removeTagsFromBulkProspectsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  changePrimaryEmailInProspectRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteSecondaryEmailRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteSecondaryPhoneRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getFilterViewListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getKanbanListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateKanbanViewRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  pinUnpinSavedFilterRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateSavedFilterRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteSavedViewRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getContactListByViewIdRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getImportedFilesListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateProspectFieldByIdRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getLongTextByProspectAndColumnIdRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getAiModelRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getAiModelTemplates: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTemplateByIdRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  saveAndDontRun: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTemplateConfigResponse: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  regenerateAiColumnCellRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEligibilityCheckRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  eligibilityCheckResponse: null,
  getAiColumnPreviewRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  createAiColumnTemplateRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateAiColumnTemplateRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteAiColumnTemplateRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  aiColPlayPauseEnrichmentRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  aiColActiveFieldsEnrichmentStatus: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updatePrimaryNumberRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  createCustomFieldRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  createdKanbanViewId: null,
  getProspectNotesRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  createProspectNotesListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteProspectNoteRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateProspectNoteRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  prospectNotePinRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateProspectNoteVisibilityRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSinglePropsectDetailPinnedFieldsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateProspectPinStatusRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getMinimalEmailAccountListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getProspectActivityCountRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  prospectPaginationData: {
    pageNum: constants.DEFAULT_PAGE_NUM,
    pageSize: constants.PROSPECT_PAGE_SIZE,
  },
  search: null,
  sequences: [],
  steps: [],
  selectedContacts: [],
  selectAllContactDetails: null,
  singleProspect: null,
  getSingleProspect: null,
  singleProspectSequenceDetails: [],
  tags: [],
  leadTags: [],
  isProspectSubscribed: null,
  addToStepRequestResultCount: {
    failed: 0,
    successful: 0,
    resultData: null,
  },
  addProspectResponse: null,
  prospectsFilters:
    getProspectsFiltersFromLocalStore() || prospectsFiltersInitialState,

  // New State
  prospects: [],
  prospectCount: 0,
  prospectKanbanData: {}, // Initialize as empty object for kanban data
  prospectKanbanCount: {}, // Initialize as empty object for kanban column counts
  prospectKanbanContactListViewData: {},
  prospectKanbanViewData: {},
  filters: {
    pageNum: 1,
    pageSize: 25,
    sortBy: ProspectSortBy.CreateAt,
    sortOrder: SortOrder.DESC,
    search: '',
    selectedFields: [],
    shouldFetchProspectList: false,
    shouldFetchProspectListCount: false,
    shouldFetchSavedFilters: false,
  },
  fields: [],
  savedFilters: [],
  selectedSavedView: null,
  prospectFiltersFieldsList: [],
  prospectFiltersFieldsAttributesValue: null,
  prospectFiltersFieldsAttributesLoading: null,
  prospectFiltersFieldsAttributesFetched: null,
  stepsDetails: [],
  importedFilesList: [],
  longText: '',
  aiTemplates: [],
  selectedAiTemplateId: 0,
  selectedAiTemplateShareableId: '',
  aiTemplatesPagination: {
    currentPage: 1,
    totalItems: 0,
    hasMore: true,
  },
  aiModelList: null,
  getTemplateById: null,
  savedNewColumn: null,
  aiColumnTemplatePreview: null,
  selectedProspectRowId: null,
  deSelectedProspectRowId: null,
  aiColumnTemplateRegenerate: null,
  regeneratedAiColCellResponse: null,
  isAiColumnLoading: false,
  currentAiColumnPayload: null,
  aiColumnloadingCell: [],
  inProgressTableCells: {
    fieldIds: [],
    prospectIds: [],
  },
  isBulkProspectSelected: false,
  aiColumnModalVisible: false,
  aiEnrichmentOpenContext: null,
  aiColEnrichedField: { fieldIds: [] },
  aiColPlayPauseEnrichmentPayload: { message: '' },
  visibleFieldIds: null,
  aiColumnStoppedFieldIds: null,
  showAiColumnWelcomeScreen: true,
  isAiColumnTemplateListCachedFromFilter: false,
  updatePrimaryNumberResponse: null,
  autoRunConditionFilterGroups: [getDefaultFilterGroupObject(1)],
  autoRunConditionFilterGroupsLogic: 'AND',
  appliedProspectEnrichmentFilters: null,
  isAiColEnrichmentFromModal: false,
  AiColEnrichmentjobIdsInProgress: [],
  enrichmentCompletedProspectIdsFromFirebase: [],
  visibleProspectIdsInProspectTable: [],
  contactIdsAndFieldIdsToRefresh: {
    contactIds: [],
    fieldIds: [],
  },
  kanbanViewList: [],
  selectedKanbanCategoryId: null,
  selectedKanbanViewId: null,
  hasAppliedKanbanSelectedFields: false,

  activeViewId: null,
  currentViewType: ViewType.List, // Default to List view
  prospectNotesList: null,
  createProspectNoteResponse: null,
  updateProspectNoteResponse: null,
  updateProspectNoteVisibilityResponse: null,
  selectedKanbanCardsFields: [],
  getSingleProspectPinnedFields: null,
  getEmailAccountList: null,

  updatedCategoryResponse: null,
  prospectToNavigateFromModal: {
    ids: [],
    columnName: '',
  },
  selectedCategoryName: null,
  showProspectDetailModal: false,
  shouldFetchInitialList: false,
  addClickedFieldId: null,
  isEmailFieldHovered: false,
  prospectViewIdFilter: null,
  prospectActivityCount: {
    notes: '',
    tasks: '',
    emails: '',
    sequences: '',
  },
  loadedSkeletonForCurrrentProspect: {
    sequences: false,
    tasks: false,
    emails: false,
  },
  addNewCategoryTriggered: false,
};

const prospectSlice = createSlice({
  name: 'prospect',
  initialState,
  reducers: {
    resetGetProspectListRequest: (state) => {
      state.getProspectListRequest = initialState.getProspectListRequest;
    },
    resetGetProspectListCountRequest: (state) => {
      state.getProspectListCountRequest =
        initialState.getProspectListCountRequest;
    },
    resetGetProspectListByIdsRequest: (state) => {
      state.getProspectListByIdsRequest =
        initialState.getProspectListByIdsRequest;
    },
    resetGetProspectFiltersFieldsListRequest: (state) => {
      state.getProspectFiltersFieldsListRequest =
        initialState.getProspectFiltersFieldsListRequest;
    },
    resetSaveProspectFiltersViewRequest: (state) => {
      state.saveProspectFiltersViewRequest =
        initialState.saveProspectFiltersViewRequest;
    },
    resetUpdateProspectFiltersViewRequest: (state) => {
      state.updateProspectFiltersViewRequest =
        initialState.updateProspectFiltersViewRequest;
    },
    resetPinUnpinSavedFilterRequest: (state) => {
      state.pinUnpinSavedFilterRequest =
        initialState.pinUnpinSavedFilterRequest;
    },
    resetUpdateSavedFilterRequest: (state) => {
      state.updateSavedFilterRequest = initialState.updateSavedFilterRequest;
    },
    resetDeleteSavedViewRequest: (state) => {
      state.deleteSavedViewRequest = initialState.deleteSavedViewRequest;
    },
    resetGetContactListByViewIdRequest: (state) => {
      state.getContactListByViewIdRequest =
        initialState.getContactListByViewIdRequest;
    },
    resetUpdatePrimaryNumberState: (state) => {
      state.updatePrimaryNumberRequest =
        initialState.updatePrimaryNumberRequest;
      state.updatePrimaryNumberResponse =
        initialState.updatePrimaryNumberResponse;
    },

    setFilters: (
      state,
      action: {
        payload: Partial<Filters>;
      },
    ) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    resetProspectFiltersSelectedFields: (state) => {
      state.filters = {
        ...state.filters,
        selectedFields: [],
      };
    },

    removeSelectedField: (state, action: { payload: string }) => {
      const updatedFields = (state.filters?.selectedFields || []).filter(
        (f) => f?.id?.toString() !== action.payload,
      );
      state.filters = {
        ...state.filters,
        selectedFields: updatedFields,
      };
      setProspectTableColumnsInLocalStorage(updatedFields);
    },

    resetProspect: (state) => {
      state.prospectCount = initialState.prospectCount;
      state.prospectPaginationData = initialState.prospectPaginationData;
      state.prospects = initialState.prospects;
      state.search = initialState.search;
      state.getProspectRequest = initialState.getProspectRequest;
    },

    resetRemoveProspectRequest: (state) => {
      state.removeProspectRequest.status = RequestStatus.Ideal;
      state.removeProspectRequest.error = null;
      state.removeProspectRequest.message = null;
    },

    resetAddToProspectForm: (state) => {
      state.addProspectManuallyRequest =
        initialState.addProspectManuallyRequest;
      state.addProspectResponse = initialState.addProspectResponse;
    },

    resetGetStepDetailsListRequest: (state) => {
      state.getStepDetailsListRequest = initialState.getStepDetailsListRequest;
    },

    resetSequenceAndStepData: (state) => {
      state.sequences = initialState.sequences;
      state.steps = initialState.steps;
      state.getStepListRequest = initialState.getStepListRequest;
      state.getStepDetailsListRequest = initialState.getStepDetailsListRequest;
      state.getSequenceListRequest = initialState.getSequenceListRequest;
      state.addToStepRequest = initialState.addToStepRequest;
      state.removeProspectRequest = initialState.removeProspectRequest;
      state.singleProspectRemoveFromSequencesRequest =
        initialState.singleProspectRemoveFromSequencesRequest;
      state.singleProspectSequencesMarkAsRepliedRequest =
        initialState.singleProspectSequencesMarkAsRepliedRequest;
      state.singleProspectSequencesMarkAsFinishedRequest =
        initialState.singleProspectSequencesMarkAsFinishedRequest;
      state.unsubscribeProspectRequest =
        initialState.unsubscribeProspectRequest;
      state.saveContactAttributesRequest =
        initialState.saveContactAttributesRequest;
      state.saveContactDealValueRequest =
        initialState.saveContactDealValueRequest;
      state.verifyProspectManuallyRequest =
        initialState.verifyProspectManuallyRequest;
    },
    resetStep: (state) => {
      state.steps = initialState.steps;
      state.addToStepRequest = initialState.addToStepRequest;
    },
    selectAllContacts: (state, action) => {
      state.selectAllContactDetails = action.payload;
    },
    clearSelection: (state) => {
      state.selectAllContactDetails = initialState.selectAllContactDetails;
      state.selectedContacts = initialState.selectedContacts;
    },
    resetSaveContactAttributesRequestStatus: (state) => {
      state.saveContactAttributesRequest =
        initialState.saveContactAttributesRequest;
    },
    resetProspectDeleteEmailRequestStatus: (state) => {
      state.deleteSecondaryEmailRequest =
        initialState.deleteSecondaryEmailRequest;
    },
    resetProspectDeletePhoneRequestStatus: (state) => {
      state.deleteSecondaryPhoneRequest =
        initialState.deleteSecondaryPhoneRequest;
    },
    resetProspectPrimaryEmailChangeRequestStatus: (state) => {
      state.changePrimaryEmailInProspectRequest =
        initialState.changePrimaryEmailInProspectRequest;
    },
    resetSingleProspect: (state) => {
      state.singleProspect = initialState.singleProspect;
    },
    checkAll: (state, action) => {
      if (action.payload.status === true) {
        if (state.selectAllContactDetails?.isAllProspectsSelected) {
          action.payload.rows.forEach(({ id: contactId }) => {
            state.selectAllContactDetails.deSelectedContactIds = state.selectAllContactDetails.deSelectedContactIds.filter(
              (id) => contactId !== id,
            );
          });
        }
        state.selectedContacts = state.selectedContacts.concat(
          action.payload.rows,
        );
      } else {
        if (state.selectAllContactDetails?.isAllProspectsSelected) {
          action.payload.rows.forEach(({ id }) => {
            state.selectAllContactDetails.deSelectedContactIds = state.selectAllContactDetails?.deSelectedContactIds.concat(
              [id],
            );
          });
        }
        state.selectedContacts = state.selectedContacts.reduce((acc, item) => {
          let isPresent = false;
          action.payload.rows.forEach(({ id }) => {
            if (id === item.id) {
              isPresent = true;
            }
          });
          if (isPresent) {
            return acc;
          }
          return [...acc, item];
        }, []);
      }
    },
    setProspectDetails: (state, action) => {
      // First try to find prospect in list view data
      let prospectWithId = state.prospects.find(
        (prospect) => prospect.id === action.payload,
      );

      // If not found in list view, try to find in kanban data
      if (!prospectWithId && state.prospectKanbanData) {
        const kanbanColumns = Object.values(state.prospectKanbanData);
        prospectWithId = kanbanColumns
          .filter((prospects) => Array.isArray(prospects))
          .flat()
          .find((prospect) => prospect.id === action.payload);
      }

      // Only set attributes if prospect is found
      if (prospectWithId) {
        const attributes = {
          id: prospectWithId.id,
          attributes: prospectWithId.attributes || [],
          tags: prospectWithId.tags || [],
          isBlacklisted: prospectWithId.isBlacklisted || false,
        };

        state.singleProspect = attributes;
        state.isProspectSubscribed = prospectWithId.isSubscribed || false;
      }
    },
    resetSelectedContacts: (state) => {
      state.selectedContacts = initialState.selectedContacts;
    },
    checkSingle: (state, action) => {
      if (action.payload.status === true) {
        if (state.selectAllContactDetails?.isAllProspectsSelected) {
          state.selectAllContactDetails.deSelectedContactIds = state.selectAllContactDetails.deSelectedContactIds.filter(
            (id) => id !== action.payload.row.id,
          );
        }
        state.selectedContacts = state.selectedContacts.concat([
          action.payload.row,
        ]);
      } else {
        if (state.selectAllContactDetails?.isAllProspectsSelected) {
          state.selectAllContactDetails.deSelectedContactIds = state.selectAllContactDetails?.deSelectedContactIds.concat(
            [action.payload.row.id],
          );
        }
        state.selectedContacts = state.selectedContacts.filter(
          ({ id }) => id !== action.payload.row.id,
        );
      }
    },
    changeProspectPagination: (state, action) => {
      state.prospectPaginationData = action.payload;
    },

    saveSearch: (state, action) => {
      state.search = action.payload;
    },
    setProspectsFilters: (state, action) => {
      state.prospectsFilters = { ...action.payload };
      setProspectsFiltersInLocalStore(action.payload);
    },
    resetProspectsFilters: (state, action) => {
      const resetOff: ProspectFilterResetArg = action.payload;
      if (resetOff === 'all') {
        state.prospectsFilters = prospectsFiltersInitialState;

        removeProspectsFiltersFromLocalStore();
      } else {
        state.prospectsFilters[resetOff] = [];
        setProspectsFiltersInLocalStore(state.prospectsFilters);
      }
    },
    resetTags: (state) => {
      state.addTagsToProspectsRequest = initialState.addTagsToProspectsRequest;
      state.removeTagsFromProspectsRequest =
        initialState.removeTagsFromProspectsRequest;
    },
    resetBulkRequests: (state) => {
      state.verifyBulkProspectsRequest =
        initialState.verifyBulkProspectsRequest;
      state.deleteBulkProspectsRequest =
        initialState.deleteBulkProspectsRequest;
      state.unsubscribeBulkProspectRequest =
        initialState.unsubscribeBulkProspectRequest;
      state.addBulkProspectsToStepRequest =
        initialState.addBulkProspectsToStepRequest;
      state.addTagsToBulkProspectsRequest =
        initialState.addTagsToBulkProspectsRequest;
      state.removeTagsFromBulkProspectsRequest =
        initialState.removeTagsFromBulkProspectsRequest;
    },
    resetUnsubscribeRequestState: (state) => {
      state.unsubscribeProspectRequest =
        initialState.unsubscribeProspectRequest;
    },
    setSelectedSavedView: (state, action) => {
      state.selectedSavedView = action.payload;
    },
    removedSelectedSavedView: (state) => {
      state.selectedSavedView = initialState.selectedSavedView;
    },
    resetFilterGroupLogic: (state) => {
      state.filters = {
        ...state.filters,
        filterGroupLogic: null,
      };
    },
    resetProspectFiltersFieldsAttributesValue: (state, action) => {
      if (
        state.prospectFiltersFieldsAttributesValue &&
        action?.payload?.resetAll
      ) {
        state.prospectFiltersFieldsAttributesValue =
          initialState.prospectFiltersFieldsAttributesValue;
        state.prospectFiltersFieldsAttributesFetched =
          initialState.prospectFiltersFieldsAttributesFetched;
        state.prospectFiltersFieldsAttributesLoading =
          initialState.prospectFiltersFieldsAttributesLoading;
        return;
      }

      if (
        state.prospectFiltersFieldsAttributesValue &&
        action?.payload?.key &&
        action?.payload?.hardReset
      ) {
        state.prospectFiltersFieldsAttributesValue[action.payload.key] = {
          meta: null,
          payload: [],
        };
        state.prospectFiltersFieldsAttributesFetched[
          action.payload.key
        ] = false;
        return;
      }

      if (
        state.prospectFiltersFieldsAttributesValue &&
        action?.payload?.key &&
        state.prospectFiltersFieldsAttributesValue?.[action?.payload?.key]
      ) {
        const { meta } = state.prospectFiltersFieldsAttributesValue?.[
          action.payload.key
        ] || { meta: null };

        if ((meta && meta.search !== '') || action.payload.key === 'tags') {
          state.prospectFiltersFieldsAttributesValue[action.payload.key] = {
            meta: null,
            payload: [],
          };
          state.prospectFiltersFieldsAttributesFetched[
            action.payload.key
          ] = false;
        }
      }
    },
    resetImportedFilesListValue: (state) => {
      state.importedFilesList = [];
    },
    resetGetLongTextByProspectAndColumnIdRequest: (state) => {
      state.getLongTextByProspectAndColumnIdRequest =
        initialState.getLongTextByProspectAndColumnIdRequest;
      state.longText = initialState.longText;
    },
    resetSaveAndDontRunRequest: (state) => {
      state.saveAndDontRun = initialState.saveAndDontRun;
    },
    setSelectedAiTemplateId: (state, action) => {
      state.selectedAiTemplateId = action.payload;
    },
    setSelectedAiTemplateShareableId: (state, action) => {
      state.selectedAiTemplateShareableId = action.payload;
    },
    resetCreateAiColumnTemplateRequest: (state) => {
      state.createAiColumnTemplateRequest.status = RequestStatus.Ideal;
      state.createAiColumnTemplateRequest.message = '';
      state.createAiColumnTemplateRequest.error = null;
    },
    resetUpdateAiColumnTemplateRequest: (state) => {
      state.updateAiColumnTemplateRequest.status = RequestStatus.Ideal;
      state.updateAiColumnTemplateRequest.message = '';
      state.updateAiColumnTemplateRequest.error = null;
    },
    resetDeleteAiColumnTemplateRequest: (state) => {
      state.deleteAiColumnTemplateRequest.status = RequestStatus.Ideal;
      state.deleteAiColumnTemplateRequest.message = '';
      state.deleteAiColumnTemplateRequest.error = null;
    },
    resetAiColumnTemplatePreview: (state) => {
      state.aiColumnTemplatePreview = { preview: '' };
    },
    resetSingleProspectDetailsRequest: (state) => {
      state.getSingleProspectDetailsRequest =
        initialState.getSingleProspectDetailsRequest;
    },
    setSelectedProspectRowId: (state, action) => {
      state.selectedProspectRowId = action.payload;
    },
    setDeSelectedProspectRowId: (state, action) => {
      state.deSelectedProspectRowId = action.payload;
    },
    resetDeSelectedProspectRowId: (state) => {
      state.deSelectedProspectRowId = initialState.deSelectedProspectRowId;
    },
    resetSelectedProspectRowId: (state) => {
      state.selectedProspectRowId = initialState.selectedProspectRowId;
    },
    resetgetTemplateByIdRequest: (state) => {
      state.getTemplateByIdRequest = initialState.getTemplateByIdRequest;
    },
    resetGetTemplateById: (state) => {
      state.getTemplateById = initialState.getTemplateById;
    },
    resetAiColumnTemplateRegenerate: (state) => {
      state.aiColumnTemplateRegenerate =
        initialState.aiColumnTemplateRegenerate;
    },
    // Manage AI Enrichment modal open context (Redux-driven)
    setAiEnrichmentOpenContext: (state, action) => {
      state.aiEnrichmentOpenContext = action.payload;
    },
    resetAiEnrichmentOpenContext: (state) => {
      state.aiEnrichmentOpenContext = initialState.aiEnrichmentOpenContext;
    },
    resetRegenerateAiColumnCellRequest: (state) => {
      state.regenerateAiColumnCellRequest =
        initialState.regenerateAiColumnCellRequest;
    },
    resetGetEligibilityCheckRequest: (state) => {
      state.getEligibilityCheckRequest =
        initialState.getEligibilityCheckRequest;
      state.eligibilityCheckResponse = initialState.eligibilityCheckResponse;
    },
    resetRegeneratedTemplateResponse: (state) => {
      state.regeneratedAiColCellResponse =
        initialState.regeneratedAiColCellResponse;
    },
    // AI Column related actions
    setIsAiColumnLoading: (state, action) => {
      state.isAiColumnLoading = action.payload;
    },
    resetCurrentAiColumnPayload: (state) => {
      state.currentAiColumnPayload = initialState.currentAiColumnPayload;
    },
    setInProgressTableCells: (state, action) => {
      state.inProgressTableCells = action.payload;
    },
    resetInProgressTableCells: (state) => {
      state.inProgressTableCells = initialState.inProgressTableCells;
    },
    setIsBulkProspectSelected: (state, action) => {
      state.isBulkProspectSelected = action.payload;
    },
    resetIsBulkProspectSelected: (state) => {
      state.isBulkProspectSelected = initialState.isBulkProspectSelected;
    },
    resetAiColPlayPauseEnrichmentRequest: (state) => {
      state.aiColPlayPauseEnrichmentRequest =
        initialState.aiColPlayPauseEnrichmentRequest;
    },
    resetGetTemplateConfigResponse: (state) => {
      state.getTemplateConfigResponse = initialState.getTemplateConfigResponse;
    },
    setAiColumnModalVisible: (state, action) => {
      state.aiColumnModalVisible = action.payload;
    },
    setIsAiColumnTemplateListCachedFromFilter: (state, action) => {
      state.isAiColumnTemplateListCachedFromFilter = action.payload;
    },
    setVisibleFieldIds: (state, action) => {
      state.visibleFieldIds = action.payload;
    },
    resetVisibleFieldIds: (state) => {
      state.visibleFieldIds = initialState.visibleFieldIds;
    },
    setAiColumnStoppedFieldIds: (state, action) => {
      state.aiColumnStoppedFieldIds = action.payload;
    },

    setShowAiColumnWelcomeScreen: (state, action) => {
      state.showAiColumnWelcomeScreen = action.payload;
    },
    resetAiColumnPreviewRequest: (state) => {
      state.getAiColumnPreviewRequest = initialState.getAiColumnPreviewRequest;
    },
    resetUpdateProspectFieldByIdRequest: (state) => {
      state.updateProspectFieldByIdRequest =
        initialState.updateProspectFieldByIdRequest;
    },
    resetSavedNewColumn: (state) => {
      state.savedNewColumn = initialState.savedNewColumn;
    },
    resetAppliedProspectEnrichmentFilters: (state) => {
      state.appliedProspectEnrichmentFilters =
        initialState.appliedProspectEnrichmentFilters;
    },
    setAutoRunConditionFilterGroups: (state, action) => {
      state.autoRunConditionFilterGroups = action.payload;
    },
    resetAutoRunConditionFilterGroups: (state) => {
      state.autoRunConditionFilterGroups = [getDefaultFilterGroupObject(1)];
    },
    setAutoRunConditionFilterGroupsLogic: (state, action) => {
      state.autoRunConditionFilterGroupsLogic = action.payload;
    },
    setIsAiColEnrichmentFromModal: (state, action) => {
      state.isAiColEnrichmentFromModal = action.payload;
    },
    setEnrichmentCompletedProspectIdsFromFirebase: (
      state,
      action: { payload: number[] },
    ) => {
      state.enrichmentCompletedProspectIdsFromFirebase = action.payload;
    },
    setVisibleProspectIdsInProspectTable: (
      state,
      action: { payload: number[] },
    ) => {
      state.visibleProspectIdsInProspectTable = action.payload;
    },
    setContactIdsAndFieldIdsToRefresh: (
      state,
      action: { payload: { contactIds: string[]; fieldIds: string[] } },
    ) => {
      state.contactIdsAndFieldIdsToRefresh = action.payload;
    },
    setSelectedKanbanCategoryId: (
      state,
      action: { payload: number | null },
    ) => {
      state.selectedKanbanCategoryId = action.payload;
    },
    resetUpdateKanbanViewRequest: (state) => {
      state.updateKanbanViewRequest = initialState.updateKanbanViewRequest;
    },
    setHasAppliedKanbanSelectedFields: (state, action) => {
      state.hasAppliedKanbanSelectedFields = action.payload;
    },
    resetHasAppliedKanbanSelectedFields: (state) => {
      state.hasAppliedKanbanSelectedFields =
        initialState.hasAppliedKanbanSelectedFields;
    },
    resetCreateCustomFieldRequest: (state) => {
      state.createCustomFieldRequest.status = RequestStatus.Ideal;
      state.createCustomFieldRequest.error = null;
      state.createdKanbanViewId = null;
      // state.isLoading = false;
    },
    setActiveViewId: (state, action) => {
      // Use Same Action for reset also
      state.activeViewId = action.payload;
    },
    setCurrentViewType: (state, action: { payload: ViewType }) => {
      state.currentViewType = action.payload;
    },
    resetCreateProspectNotesListRequest: (state) => {
      state.createProspectNotesListRequest =
        initialState.createProspectNotesListRequest;
      state.createProspectNoteResponse = null;
      state.updateProspectNoteResponse = null;
    },
    resetDeleteProspectNoteRequest: (state) => {
      state.deleteProspectNoteRequest = initialState.deleteProspectNoteRequest;
    },
    resetProspectNotePinRequest: (state) => {
      state.prospectNotePinRequest = initialState.prospectNotePinRequest;
    },
    resetUpdateProspectNoteRequest: (state) => {
      state.updateProspectNoteRequest = initialState.updateProspectNoteRequest;
      state.updateProspectNoteResponse = null;
    },
    resetGetProspectNotesRequest: (state) => {
      state.getProspectNotesRequest = initialState.getProspectNotesRequest;
    },
    resetUpdateProspectNoteVisibilityRequest: (state) => {
      state.updateProspectNoteVisibilityRequest =
        initialState.updateProspectNoteVisibilityRequest;
      state.updateProspectNoteVisibilityResponse = null;
    },

    setSelectedKanbanCardsFields: (state, action) => {
      state.selectedKanbanCardsFields = action?.payload || [];
    },

    setKanbanViewList: (state, action) => {
      state.kanbanViewList = action?.payload || [];
    },
    resetKanbanViewList: (state) => {
      state.kanbanViewList = initialState.kanbanViewList;
    },

    setProspectKanbanCount: (state, action) => {
      state.prospectKanbanCount = action?.payload || {};
    },

    setUpdatedCategoryResponse: (state, action) => {
      state.updatedCategoryResponse = action?.payload || null;
    },

    setProspectToNavigateFromModal: (state, action) => {
      state.prospectToNavigateFromModal = action?.payload || {
        ids: [],
        columnName: '',
      };
    },
    appendProspectIdsToNavigateFromModal: (state, action) => {
      const newIds = action?.payload || [];
      if (Array.isArray(newIds) && newIds.length > 0) {
        const existingIds = state.prospectToNavigateFromModal?.ids || [];
        // Avoid duplicates
        const uniqueNewIds = newIds.filter((id) => !existingIds.includes(id));
        state.prospectToNavigateFromModal = {
          ...state.prospectToNavigateFromModal,
          ids: [...existingIds, ...uniqueNewIds],
        };
      }
    },
    setSelectedCategoryName: (state, action) => {
      state.selectedCategoryName = action?.payload;
    },
    setShowProspectDetailModal: (state, action) => {
      state.showProspectDetailModal = action?.payload;
    },
    setShouldFetchInitialList: (state, action) => {
      state.shouldFetchInitialList = action?.payload;
    },
    setAddClickedFieldId: (state, action) => {
      state.addClickedFieldId = action?.payload || null;
    },
    setEmailFieldHovered: (state, action) => {
      state.isEmailFieldHovered = action?.payload || false;
    },
    setProspectViewIdFilter: (state, action) => {
      state.prospectViewIdFilter = action.payload;
    },
    setProspectActivityCount: (state, action) => {
      state.prospectActivityCount =
        action?.payload || initialState.prospectActivityCount;
    },
    resetLoadedSkeletonForCurrrentProspect: (state) => {
      state.loadedSkeletonForCurrrentProspect =
        initialState.loadedSkeletonForCurrrentProspect;
    },
    setLoadedSkeletonForCurrrentProspect: (state, action) => {
      state.loadedSkeletonForCurrrentProspect = {
        ...state.loadedSkeletonForCurrrentProspect,
        ...action?.payload,
      };
    },
    resetGetSingleProspectSequencesDetailsRequest: (state) => {
      state.getSingleProspectSequencesDetailsRequest =
        initialState.getSingleProspectSequencesDetailsRequest;
    },
    setAddNewCategoryTriggered: (state, action) => {
      state.addNewCategoryTriggered = action?.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Prospect List
    builder.addCase(getProspectListRequest.pending, (state) => {
      state.getProspectListRequest.status = RequestStatus.Pending;
      state.getProspectListRequest.error = null;
    });
    builder.addCase(getProspectListRequest.fulfilled, (state, action) => {
      state.getProspectListRequest.status = RequestStatus.Succeeded;
      state.getProspectListRequest.message = action.payload.message;

      const {
        payload: { payload },
      } = action;

      // Get viewType from the original request to handle different response structures
      const viewType = action.meta.arg?.viewType;

      const sorting = payload?.view?.filterCriteria?.sorting;
      const parsedSorting = parseSortingFromResponse(sorting);

      if (parsedSorting) {
        if (
          state.filters.sortBy !== ProspectSortBy.None ||
          parsedSorting.sortBy === ProspectSortBy.None
        ) {
          state.filters.sortBy = parsedSorting.sortBy;
          state.filters.sortOrder = parsedSorting.sortOrder;
        }
      }

      if (viewType === ViewType.Kanban) {
        // Kanban view: payload structure is { "Closed": [], "Do Not Contact": [], "Interested": [], ... }
        // Column names are dynamic keys, values are arrays of prospects
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
          // Filter out jobIdsInProgress if it exists, as it's metadata not a column
          const { jobIdsInProgress, ...kanbanColumns } = payload?.contacts;

          // Check if this is a pagination request for a specific column (activeColumns specified)
          const requestArgs = action.meta.arg || {};
          const {activeColumns} = requestArgs;
          const pageNum = requestArgs.pageNum || 0;

          // If activeColumns is specified and pageNum > 0, merge data instead of replacing
          if (
            activeColumns &&
            Array.isArray(activeColumns) &&
            activeColumns.length > 0 &&
            pageNum > 0
          ) {
            // This is a pagination request for specific columns - merge with existing data
            const existingKanbanData = state.prospectKanbanData || {};
            const mergedKanbanData = { ...existingKanbanData };

            // Merge each requested column's data
            activeColumns.forEach((columnKey: string) => {
              const newColumnData = kanbanColumns[columnKey];
              const existingColumnData = existingKanbanData[columnKey] || [];

              if (Array.isArray(newColumnData) && newColumnData.length > 0) {
                // Append new prospects to existing column data, avoiding duplicates
                const existingIds = new Set(
                  existingColumnData.map((p: any) => p.id).filter(Boolean),
                );
                const uniqueNewProspects = newColumnData.filter(
                  (p: any) => p.id && !existingIds.has(p.id),
                );
                mergedKanbanData[columnKey] = [
                  ...existingColumnData,
                  ...uniqueNewProspects,
                ];
              } else if (
                Array.isArray(existingColumnData) &&
                existingColumnData.length > 0
              ) {
                // Keep existing data if no new data
                mergedKanbanData[columnKey] = existingColumnData;
              }
            });

            state.prospectKanbanData = mergedKanbanData;
            // Update contactListViewData but preserve other columns
            if (state.prospectKanbanContactListViewData?.contacts) {
              state.prospectKanbanContactListViewData = {
                ...state.prospectKanbanContactListViewData,
                contacts: {
                  ...state.prospectKanbanContactListViewData.contacts,
                  ...mergedKanbanData,
                },
              };
            } else {
              state.prospectKanbanContactListViewData = {
                ...payload,
                contacts: mergedKanbanData,
              };
            }
          } else {
            // First page load or full refresh - replace all data
            state.prospectKanbanData = kanbanColumns;
            state.prospectKanbanContactListViewData = payload;
          }

          state.AiColEnrichmentjobIdsInProgress = jobIdsInProgress || [];
          if (payload?.view?.kanbanViewId) {
            state.selectedKanbanViewId = payload.view.kanbanViewId;
          }
        } else {
          // If payload is an array or invalid, initialize as empty object
          state.prospectKanbanData = {};
          state.prospectKanbanContactListViewData = {};
          state.AiColEnrichmentjobIdsInProgress = [];
        }
        // Don't set state.prospects for kanban (it uses prospectKanbanData instead)
        // Don't extract visible prospect IDs for kanban view
      } else {
        // List view: payload has { results: [...], jobIdsInProgress: [...] }
        const results = payload?.results;
        state.prospects = Array.isArray(results) ? results : [];
        state.AiColEnrichmentjobIdsInProgress = payload?.jobIdsInProgress || [];

        // Extract and store visible prospect IDs (only for list view)
        if (Array.isArray(results) && results.length > 0) {
          const prospectIds = results
            .map((prospect: any) => prospect.id)
            .filter((id: any) => id !== undefined);
          state.visibleProspectIdsInProspectTable = prospectIds;
        } else {
          state.visibleProspectIdsInProspectTable = [];
        }
      }
    });
    builder.addCase(getProspectListRequest.rejected, (state, action) => {
      state.getProspectListRequest.status = RequestStatus.Failed;
      state.getProspectListRequest.error = action.payload;
    });

    // Get Prospect List Count
    builder.addCase(getProspectListCountRequest.pending, (state) => {
      state.getProspectListCountRequest.status = RequestStatus.Pending;
      state.getProspectListCountRequest.error = null;
    });
    builder.addCase(getProspectListCountRequest.fulfilled, (state, action) => {
      state.getProspectListCountRequest.status = RequestStatus.Succeeded;
      state.getProspectListCountRequest.message = action.payload.message;

      const {
        payload: { payload },
      } = action;
      // Get viewType from the original request to handle different response structures
      const viewType = action.meta.arg?.viewType;

      if (viewType === ViewType.Kanban) {
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
          state.prospectKanbanCount = payload;
        } else {
          state.prospectKanbanCount = {};
        }
      } else {
        // List view: payload is a single number
        state.prospectCount = payload;
      }
    });
    builder.addCase(getProspectListCountRequest.rejected, (state, action) => {
      state.getProspectListCountRequest.status = RequestStatus.Failed;
      state.getProspectListCountRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Prospect List By Ids
    builder.addCase(getProspectListByIdsRequest.pending, (state) => {
      state.getProspectListByIdsRequest.status = RequestStatus.Pending;
      state.getProspectListByIdsRequest.error = null;
    });
    builder.addCase(getProspectListByIdsRequest.fulfilled, (state, action) => {
      state.getProspectListByIdsRequest.status = RequestStatus.Succeeded;
      state.getProspectListByIdsRequest.message = action.payload.message;
      state.prospects = getMergedProspects(
        state.prospects,
        action.payload.payload?.results || action.payload.payload,
      );
      state.AiColEnrichmentjobIdsInProgress =
        action.payload.payload?.jobIdsInProgress || [];
    });
    builder.addCase(getProspectListByIdsRequest.rejected, (state, action) => {
      state.getProspectListByIdsRequest.status = RequestStatus.Failed;
      state.getProspectListByIdsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Prospect Filter Fields List
    builder.addCase(getProspectFiltersFieldsListRequest.pending, (state) => {
      state.getProspectFiltersFieldsListRequest.status = RequestStatus.Pending;
      state.getProspectFiltersFieldsListRequest.error = null;
    });
    builder.addCase(
      getProspectFiltersFieldsListRequest.fulfilled,
      (state, action) => {
        state.getProspectFiltersFieldsListRequest.status =
          RequestStatus.Succeeded;
        state.getProspectFiltersFieldsListRequest.message =
          action.payload.message;
        state.prospectFiltersFieldsList = getSortedProspectFilterFields(
          action.payload.payload.fields,
        );
      },
    );
    builder.addCase(
      getProspectFiltersFieldsListRequest.rejected,
      (state, action) => {
        state.getProspectFiltersFieldsListRequest.status = RequestStatus.Failed;
        state.getProspectFiltersFieldsListRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Prospect Filter Fields Values
    builder.addCase(
      getProspectFiltersFieldValuesRequest.pending,
      (state, action) => {
        state.getProspectFiltersFieldValuesRequest.status =
          RequestStatus.Pending;
        state.getProspectFiltersFieldValuesRequest.error = null;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          [action.meta.arg.key]: true,
        };
        state.prospectFiltersFieldsAttributesFetched = {
          ...state.prospectFiltersFieldsAttributesFetched,
          [action.meta.arg.key]: true,
        };
      },
    );
    builder.addCase(
      getProspectFiltersFieldValuesRequest.fulfilled,
      (state, action) => {
        state.getProspectFiltersFieldValuesRequest.status =
          RequestStatus.Succeeded;
        state.getProspectFiltersFieldValuesRequest.message =
          action.payload.message;
        state.prospectFiltersFieldsAttributesValue = {
          ...state.prospectFiltersFieldsAttributesValue,
          [action.meta.arg.key]: {
            meta: {
              ...action.meta.arg,
              hasMoreData: action.payload.payload.hasMoreData,
            },
            payload:
              action.meta.arg.page === 1
                ? action.payload.payload.prospectValues
                : [
                    ...(state.prospectFiltersFieldsAttributesValue?.[
                      action.meta.arg.key
                    ]?.payload || []),
                    ...action.payload.payload.prospectValues,
                  ],
          },
        };
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          [action.meta.arg.key]: false,
        };
      },
    );
    builder.addCase(
      getProspectFiltersFieldValuesRequest.rejected,
      (state, action) => {
        state.getProspectFiltersFieldValuesRequest.status =
          RequestStatus.Failed;
        state.getProspectFiltersFieldValuesRequest.error =
          !action.payload.isHandled && action.payload;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          [action.meta.arg.key]: false,
        };
      },
    );

    builder.addCase(
      getProspectLeadFinderListsRequest.pending,
      (state, action) => {
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          [action.meta.arg.key]: true,
        };
        state.prospectFiltersFieldsAttributesFetched = {
          ...state.prospectFiltersFieldsAttributesFetched,
          [action.meta.arg.key]: true,
        };
      },
    );
    builder.addCase(
      getProspectLeadFinderListsRequest.fulfilled,
      (state, action) => {
        const lists = Array.isArray(action.payload?.payload)
          ? action.payload.payload
          : [];
        state.prospectFiltersFieldsAttributesValue = {
          ...state.prospectFiltersFieldsAttributesValue,
          [action.meta.arg.key]: {
            meta: null,
            payload: lists.map((list: any) => ({
              name: list?.name,
              value: list?.id?.toString(),
            })),
          },
        };
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          [action.meta.arg.key]: false,
        };
      },
    );
    builder.addCase(
      getProspectLeadFinderListsRequest.rejected,
      (state, action) => {
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          [action.meta.arg.key]: false,
        };
      },
    );

    // Get Prospect Filter Field Attribute Values
    builder.addCase(
      getProspectFiltersAttributeFieldValuesRequest.pending,
      (state, action) => {
        state.getProspectFiltersAttributeFieldValuesRequest.status =
          RequestStatus.Pending;
        state.getProspectFiltersAttributeFieldValuesRequest.error = null;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          [action.meta.arg.key]: true,
        };
        state.prospectFiltersFieldsAttributesFetched = {
          ...state.prospectFiltersFieldsAttributesFetched,
          [action.meta.arg.key]: true,
        };
      },
    );
    builder.addCase(
      getProspectFiltersAttributeFieldValuesRequest.fulfilled,
      (state, action) => {
        state.getProspectFiltersAttributeFieldValuesRequest.status =
          RequestStatus.Succeeded;
        state.getProspectFiltersAttributeFieldValuesRequest.message =
          action.payload.message;
        state.prospectFiltersFieldsAttributesValue = {
          ...state.prospectFiltersFieldsAttributesValue,
          [action.meta.arg.key]: {
            meta: {
              ...action.meta.arg,
              hasMoreData: action.payload.payload.hasMoreData,
            },
            payload:
              action.meta.arg.page === 1
                ? action.payload.payload.prospectAttributeValues
                : [
                    ...(state.prospectFiltersFieldsAttributesValue?.[
                      action.meta.arg.key
                    ]?.payload || []),
                    ...action.payload.payload.prospectAttributeValues,
                  ],
          },
        };
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          [action.meta.arg.key]: false,
        };
      },
    );
    builder.addCase(
      getProspectFiltersAttributeFieldValuesRequest.rejected,
      (state, action) => {
        state.getProspectFiltersAttributeFieldValuesRequest.status =
          RequestStatus.Failed;
        state.getProspectFiltersAttributeFieldValuesRequest.error =
          !action.payload.isHandled && action.payload;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          [action.meta.arg.key]: false,
        };
      },
    );

    // Get Prospect Filter Sequence List
    builder.addCase(getProspectFiltersSequencesListRequest.pending, (state) => {
      state.getProspectFiltersSequencesListRequest.status =
        RequestStatus.Pending;
      state.getProspectFiltersSequencesListRequest.error = null;
      state.prospectFiltersFieldsAttributesLoading = {
        ...state.prospectFiltersFieldsAttributesLoading,
        sequence: true,
      };
      state.prospectFiltersFieldsAttributesFetched = {
        ...state.prospectFiltersFieldsAttributesFetched,
        sequence: true,
      };
    });
    builder.addCase(
      getProspectFiltersSequencesListRequest.fulfilled,
      (state, action) => {
        state.getProspectFiltersSequencesListRequest.status =
          RequestStatus.Succeeded;
        state.getProspectFiltersSequencesListRequest.message =
          action.payload.message;
        state.prospectFiltersFieldsAttributesValue = {
          ...state.prospectFiltersFieldsAttributesValue,
          sequence: {
            meta: {
              ...action.meta.arg,
              key: 'sequence',
              hasMoreData: action.payload.payload.hasMoreData,
            },
            payload:
              action.meta.arg.page === 1
                ? action.payload.payload.sequences?.map((sequence) => ({
                    name: sequence.title,
                    value: sequence.id.toString(),
                  }))
                : [
                    ...(state.prospectFiltersFieldsAttributesValue?.sequence
                      ?.payload || []),
                    ...action.payload.payload.sequences?.map((sequence) => ({
                      name: sequence.title,
                      value: sequence.id.toString(),
                    })),
                  ],
          },
        };
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          sequence: false,
        };
      },
    );
    builder.addCase(
      getProspectFiltersSequencesListRequest.rejected,
      (state, action) => {
        state.getProspectFiltersSequencesListRequest.status =
          RequestStatus.Failed;
        state.getProspectFiltersSequencesListRequest.error =
          !action.payload.isHandled && action.payload;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          sequence: false,
        };
      },
    );

    // Get Prospect Filter Clients List
    builder.addCase(getProspectFiltersClientsListRequest.pending, (state) => {
      state.getProspectFiltersClientsListRequest.status = RequestStatus.Pending;
      state.getProspectFiltersClientsListRequest.error = null;
      state.prospectFiltersFieldsAttributesLoading = {
        ...state.prospectFiltersFieldsAttributesLoading,
        clientSequenceAssociation: true,
      };
      state.prospectFiltersFieldsAttributesFetched = {
        ...state.prospectFiltersFieldsAttributesFetched,
        clientSequenceAssociation: true,
      };
    });
    builder.addCase(
      getProspectFiltersClientsListRequest.fulfilled,
      (state, action) => {
        state.getProspectFiltersClientsListRequest.status =
          RequestStatus.Succeeded;
        state.getProspectFiltersClientsListRequest.message =
          action.payload.message;
        state.prospectFiltersFieldsAttributesValue = {
          ...state.prospectFiltersFieldsAttributesValue,
          clientSequenceAssociation: {
            meta: {
              ...action.meta.arg,
              key: 'clientSequenceAssociation',
              hasMoreData: action.payload.payload.hasMoreData,
            },
            payload:
              action.meta.arg.page === 1
                ? action.payload.payload.clients?.map((client) => ({
                    name: `${client.companyName || ''} (${
                      client.email
                    })`.trim(),
                    value: client.id.toString(),
                  }))
                : [
                    ...(state.prospectFiltersFieldsAttributesValue
                      ?.clientSequenceAssociation?.payload || []),
                    ...action.payload.payload.clients?.map((client) => ({
                      name: `${client.companyName || ''} (${
                        client.email
                      })`.trim(),
                      value: client.id.toString(),
                    })),
                  ],
          },
        };
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          clientSequenceAssociation: false,
        };
      },
    );
    builder.addCase(
      getProspectFiltersClientsListRequest.rejected,
      (state, action) => {
        state.getProspectFiltersClientsListRequest.status =
          RequestStatus.Failed;
        state.getProspectFiltersClientsListRequest.error =
          !action.payload.isHandled && action.payload;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          clientSequenceAssociation: false,
        };
      },
    );

    // Get Prospect Filter Tags List
    builder.addCase(getProspectFiltersTagsListRequest.pending, (state) => {
      state.getProspectFiltersTagsListRequest.status = RequestStatus.Pending;
      state.getProspectFiltersTagsListRequest.error = null;
      state.prospectFiltersFieldsAttributesLoading = {
        ...state.prospectFiltersFieldsAttributesLoading,
        tags: true,
      };
      state.prospectFiltersFieldsAttributesFetched = {
        ...state.prospectFiltersFieldsAttributesFetched,
        tags: true,
      };
    });
    builder.addCase(
      getProspectFiltersTagsListRequest.fulfilled,
      (state, action) => {
        state.getProspectFiltersTagsListRequest.status =
          RequestStatus.Succeeded;
        state.getProspectFiltersTagsListRequest.message =
          action.payload.message;
        state.prospectFiltersFieldsAttributesValue = {
          ...state.prospectFiltersFieldsAttributesValue,
          tags: {
            meta: null,
            payload: [
              ...(state.prospectFiltersFieldsAttributesValue?.tags?.payload ||
                []),
              ...action.payload.payload?.map((tag) => ({
                name: tag.name,
                value: tag.id.toString(),
              })),
            ],
          },
        };
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          tags: false,
        };
      },
    );
    builder.addCase(
      getProspectFiltersTagsListRequest.rejected,
      (state, action) => {
        state.getProspectFiltersTagsListRequest.status = RequestStatus.Failed;
        state.getProspectFiltersTagsListRequest.error =
          !action.payload.isHandled && action.payload;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          tags: false,
        };
      },
    );

    // Get Prospect Filter Owners List
    builder.addCase(getProspectFiltersOwnersListRequest.pending, (state) => {
      state.getProspectFiltersOwnersListRequest.status = RequestStatus.Pending;
      state.getProspectFiltersOwnersListRequest.error = null;
      state.prospectFiltersFieldsAttributesLoading = {
        ...state.prospectFiltersFieldsAttributesLoading,
        owners: true,
      };
      state.prospectFiltersFieldsAttributesFetched = {
        ...state.prospectFiltersFieldsAttributesFetched,
        owners: true,
      };
    });
    builder.addCase(
      getProspectFiltersOwnersListRequest.fulfilled,
      (state, action) => {
        state.getProspectFiltersOwnersListRequest.status =
          RequestStatus.Succeeded;
        state.getProspectFiltersOwnersListRequest.message =
          action.payload.message;
        state.prospectFiltersFieldsAttributesValue = {
          ...state.prospectFiltersFieldsAttributesValue,
          owners: {
            meta: null,
            payload: [
              ...(state.prospectFiltersFieldsAttributesValue?.owners?.payload ||
                []),
              ...action.payload.payload,
            ],
          },
        };
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          owners: false,
        };
      },
    );
    builder.addCase(
      getProspectFiltersOwnersListRequest.rejected,
      (state, action) => {
        state.getProspectFiltersOwnersListRequest.status = RequestStatus.Failed;
        state.getProspectFiltersOwnersListRequest.error =
          !action.payload.isHandled && action.payload;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          owners: false,
        };
      },
    );

    // Get Prospect Filter Owners List For Agency
    builder.addCase(
      getProspectFiltersOwnersListForAgencyRequest.pending,
      (state) => {
        state.getProspectFiltersOwnersListForAgencyRequest.status =
          RequestStatus.Pending;
        state.getProspectFiltersOwnersListForAgencyRequest.error = null;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          owners: true,
        };
        state.prospectFiltersFieldsAttributesFetched = {
          ...state.prospectFiltersFieldsAttributesFetched,
          owners: true,
        };
      },
    );
    builder.addCase(
      getProspectFiltersOwnersListForAgencyRequest.fulfilled,
      (state, action) => {
        state.getProspectFiltersOwnersListForAgencyRequest.status =
          RequestStatus.Succeeded;
        state.getProspectFiltersOwnersListForAgencyRequest.message =
          action.payload.message;

        state.prospectFiltersFieldsAttributesValue = {
          ...state.prospectFiltersFieldsAttributesValue,
          owners: {
            meta: null,
            payload: [
              ...(state.prospectFiltersFieldsAttributesValue?.owners?.payload ||
                []),
              ...action.payload.payload?.map((owner) => ({
                name:
                  `${owner?.firstName || ''} ${owner?.lastName || ''}`.trim() ??
                  owner?.email,
                value: owner?.id?.toString() || '',
              })),
            ],
          },
        };
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          owners: false,
        };
      },
    );
    builder.addCase(
      getProspectFiltersOwnersListForAgencyRequest.rejected,
      (state, action) => {
        state.getProspectFiltersOwnersListForAgencyRequest.status =
          RequestStatus.Failed;
        state.getProspectFiltersOwnersListForAgencyRequest.error =
          !action.payload.isHandled && action.payload;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          owners: false,
        };
      },
    );

    // Get Prospect Filter Outcomes List
    builder.addCase(getProspectFiltersOutcomeListRequest.pending, (state) => {
      state.getProspectFiltersOutcomeListRequest.status = RequestStatus.Pending;
      state.getProspectFiltersOutcomeListRequest.error = null;
      state.prospectFiltersFieldsAttributesLoading = {
        ...state.prospectFiltersFieldsAttributesLoading,
        outcomes: true,
      };
      state.prospectFiltersFieldsAttributesFetched = {
        ...state.prospectFiltersFieldsAttributesFetched,
        outcomes: true,
      };
    });
    builder.addCase(
      getProspectFiltersOutcomeListRequest.fulfilled,
      (state, action) => {
        state.getProspectFiltersOutcomeListRequest.status =
          RequestStatus.Succeeded;
        state.getProspectFiltersOutcomeListRequest.message =
          action.payload.message;
        state.prospectFiltersFieldsAttributesValue = {
          ...state.prospectFiltersFieldsAttributesValue,
          outcomes: {
            meta: null,
            payload: [
              ...(state.prospectFiltersFieldsAttributesValue?.outcomes
                ?.payload || []),
              ...action.payload.payload?.map((outcome) => ({
                name: outcome.name,
                value: outcome.id.toString(),
              })),
            ],
          },
        };
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          outcomes: false,
        };
      },
    );
    builder.addCase(
      getProspectFiltersOutcomeListRequest.rejected,
      (state, action) => {
        state.getProspectFiltersOutcomeListRequest.status =
          RequestStatus.Failed;
        state.getProspectFiltersOutcomeListRequest.error =
          !action.payload.isHandled && action.payload;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          outcomes: false,
        };
      },
    );

    // Get Prospect Filter Subsequences List
    builder.addCase(
      getProspectFiltersSubsequenceListRequest.pending,
      (state) => {
        state.getProspectFiltersSubsequenceListRequest.status =
          RequestStatus.Pending;
        state.getProspectFiltersSubsequenceListRequest.error = null;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          subSequence: true,
        };
        state.prospectFiltersFieldsAttributesFetched = {
          ...state.prospectFiltersFieldsAttributesFetched,
          subSequence: true,
        };
      },
    );
    builder.addCase(
      getProspectFiltersSubsequenceListRequest.fulfilled,
      (state, action) => {
        state.getProspectFiltersSubsequenceListRequest.status =
          RequestStatus.Succeeded;
        state.getProspectFiltersSubsequenceListRequest.message =
          action.payload.message;
        state.prospectFiltersFieldsAttributesValue = {
          ...state.prospectFiltersFieldsAttributesValue,
          subSequence: {
            meta: null,
            payload: [
              ...(state.prospectFiltersFieldsAttributesValue?.subSequence
                ?.payload || []),
              ...action.payload.payload?.sequences?.map((seq) => ({
                key: seq.id.toString(),
                value: seq.title,
                items: seq.subsequences.map((subseq) => ({
                  key: subseq.id.toString(),
                  value: subseq.title,
                })),
              })),
            ],
          },
        };
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          subSequence: false,
        };
      },
    );
    builder.addCase(
      getProspectFiltersSubsequenceListRequest.rejected,
      (state, action) => {
        state.getProspectFiltersSubsequenceListRequest.status =
          RequestStatus.Failed;
        state.getProspectFiltersSubsequenceListRequest.error =
          !action.payload.isHandled && action.payload;
        state.prospectFiltersFieldsAttributesLoading = {
          ...state.prospectFiltersFieldsAttributesLoading,
          subSequence: false,
        };
      },
    );

    // Save Prospect Filters View
    builder.addCase(saveProspectFiltersViewRequest.pending, (state) => {
      state.saveProspectFiltersViewRequest.status = RequestStatus.Pending;
      state.saveProspectFiltersViewRequest.error = null;
    });
    builder.addCase(
      saveProspectFiltersViewRequest.fulfilled,
      (state, action) => {
        state.saveProspectFiltersViewRequest.status = RequestStatus.Succeeded;
        state.saveProspectFiltersViewRequest.message = action.payload.message;
        state.selectedSavedView = action.payload.payload;
      },
    );
    builder.addCase(
      saveProspectFiltersViewRequest.rejected,
      (state, action) => {
        state.saveProspectFiltersViewRequest.status = RequestStatus.Failed;
        state.saveProspectFiltersViewRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Update Prospect Filters View
    builder.addCase(updateProspectFiltersViewRequest.pending, (state) => {
      state.updateProspectFiltersViewRequest.status = RequestStatus.Pending;
      state.updateProspectFiltersViewRequest.error = null;
    });
    builder.addCase(
      updateProspectFiltersViewRequest.fulfilled,
      (state, action) => {
        state.updateProspectFiltersViewRequest.status = RequestStatus.Succeeded;
        state.updateProspectFiltersViewRequest.message = action.payload.message;
      },
    );
    builder.addCase(
      updateProspectFiltersViewRequest.rejected,
      (state, action) => {
        state.updateProspectFiltersViewRequest.status = RequestStatus.Failed;
        state.updateProspectFiltersViewRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Delete Prospect Filters View
    builder.addCase(deleteProspectFiltersViewRequest.pending, (state) => {
      state.deleteProspectFiltersViewRequest.status = RequestStatus.Pending;
      state.deleteProspectFiltersViewRequest.error = null;
    });
    builder.addCase(
      deleteProspectFiltersViewRequest.fulfilled,
      (state, action) => {
        state.deleteProspectFiltersViewRequest.status = RequestStatus.Succeeded;
        state.deleteProspectFiltersViewRequest.message = action.payload.message;
      },
    );
    builder.addCase(
      deleteProspectFiltersViewRequest.rejected,
      (state, action) => {
        state.deleteProspectFiltersViewRequest.status = RequestStatus.Failed;
        state.deleteProspectFiltersViewRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Prospect Request
    builder.addCase(getProspectRequest.pending, (state) => {
      state.getProspectRequest.status = RequestStatus.Pending;
      state.getProspectRequest.error = null;
    });
    builder.addCase(getProspectRequest.fulfilled, (state, action) => {
      state.getProspectRequest.status = RequestStatus.Succeeded;
      state.getProspectRequest.message = action.payload.message;
      state.prospects = action.payload.payload.contacts;
    });
    builder.addCase(getProspectRequest.rejected, (state, action) => {
      state.getProspectRequest.status = RequestStatus.Failed;
      state.getProspectRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Prospect Count Request
    builder.addCase(getProspectCountRequest.pending, (state) => {
      state.getProspectCountRequest.status = RequestStatus.Pending;
      state.getProspectCountRequest.error = null;
    });
    builder.addCase(getProspectCountRequest.fulfilled, (state, action) => {
      state.getProspectCountRequest.status = RequestStatus.Succeeded;
      state.getProspectCountRequest.message = action.payload.message;
      state.prospectCount = action.payload.payload;
    });
    builder.addCase(getProspectCountRequest.rejected, (state, action) => {
      state.getProspectCountRequest.status = RequestStatus.Failed;
      state.getProspectCountRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Sequence List
    builder.addCase(getSequenceListRequest.pending, (state) => {
      state.getSequenceListRequest.status = RequestStatus.Pending;
      state.getSequenceListRequest.error = null;
    });
    builder.addCase(getSequenceListRequest.fulfilled, (state, action) => {
      state.getSequenceListRequest.status = RequestStatus.Succeeded;
      state.getSequenceListRequest.message = action.payload.message;
      state.sequences = action.payload.payload;
    });
    builder.addCase(getSequenceListRequest.rejected, (state, action) => {
      state.getSequenceListRequest.status = RequestStatus.Failed;
      state.getSequenceListRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Step List
    builder.addCase(getStepListRequest.pending, (state) => {
      state.getStepListRequest.status = RequestStatus.Pending;
      state.getStepListRequest.error = null;
    });
    builder.addCase(getStepListRequest.fulfilled, (state, action) => {
      state.getStepListRequest.status = RequestStatus.Succeeded;
      state.getStepListRequest.message = action.payload.message;
      state.steps = action.payload.payload;
    });
    builder.addCase(getStepListRequest.rejected, (state, action) => {
      state.getStepListRequest.status = RequestStatus.Failed;
      state.getStepListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Step  Details List
    builder.addCase(getStepDetailsListRequest.pending, (state) => {
      state.getStepDetailsListRequest.status = RequestStatus.Pending;
      state.getStepDetailsListRequest.error = null;
    });
    builder.addCase(getStepDetailsListRequest.fulfilled, (state, action) => {
      state.getStepDetailsListRequest.status = RequestStatus.Succeeded;
      state.getStepDetailsListRequest.message = action.payload.message;
      state.stepsDetails = action.payload.payload;
    });
    builder.addCase(getStepDetailsListRequest.rejected, (state, action) => {
      state.getStepDetailsListRequest.status = RequestStatus.Failed;
      state.getStepDetailsListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Remove Prospect
    builder.addCase(removeProspectRequest.pending, (state) => {
      state.removeProspectRequest.status = RequestStatus.Pending;
      state.removeProspectRequest.error = null;
    });
    builder.addCase(removeProspectRequest.fulfilled, (state, action) => {
      state.removeProspectRequest.status = RequestStatus.Succeeded;
      state.removeProspectRequest.message = action.payload.message;
      state.steps = action.payload.payload;
    });
    builder.addCase(removeProspectRequest.rejected, (state, action) => {
      state.removeProspectRequest.status = RequestStatus.Failed;
      state.removeProspectRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Unsubscribe Prospect Request
    builder.addCase(unsubscribeProspectRequest.pending, (state) => {
      state.unsubscribeProspectRequest.status = RequestStatus.Pending;
      state.unsubscribeProspectRequest.error = null;
    });
    builder.addCase(unsubscribeProspectRequest.fulfilled, (state, action) => {
      state.unsubscribeProspectRequest.status = RequestStatus.Succeeded;
      state.unsubscribeProspectRequest.message = action.payload.message;
      state.steps = action.payload.payload;
    });
    builder.addCase(unsubscribeProspectRequest.rejected, (state, action) => {
      state.unsubscribeProspectRequest.status = RequestStatus.Failed;
      state.unsubscribeProspectRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Single Contact Details
    builder.addCase(getSingleProspectDetailsRequest.pending, (state) => {
      state.getSingleProspectDetailsRequest.status = RequestStatus.Pending;
      state.getSingleProspectDetailsRequest.error = null;
    });
    builder.addCase(
      getSingleProspectDetailsRequest.fulfilled,
      (state, action) => {
        state.getSingleProspectDetailsRequest.status = RequestStatus.Succeeded;
        state.getSingleProspectDetailsRequest.message = action.payload.message;
        state.singleProspect = action.payload.payload;
        state.getSingleProspect = action.payload.payload;
      },
    );
    builder.addCase(
      getSingleProspectDetailsRequest.rejected,
      (state, action) => {
        state.getSingleProspectDetailsRequest.status = RequestStatus.Failed;
        state.getSingleProspectDetailsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Single Contact Sequence Details
    builder.addCase(
      getSingleProspectSequencesDetailsRequest.pending,
      (state) => {
        state.getSingleProspectSequencesDetailsRequest.status =
          RequestStatus.Pending;
        state.getSingleProspectSequencesDetailsRequest.error = null;
      },
    );
    builder.addCase(
      getSingleProspectSequencesDetailsRequest.fulfilled,
      (state, action) => {
        state.getSingleProspectSequencesDetailsRequest.status =
          RequestStatus.Succeeded;
        state.getSingleProspectSequencesDetailsRequest.message =
          action.payload.message;
        state.singleProspectSequenceDetails = action.payload.payload;
      },
    );
    builder.addCase(
      getSingleProspectSequencesDetailsRequest.rejected,
      (state, action) => {
        state.getSingleProspectSequencesDetailsRequest.status =
          RequestStatus.Failed;
        state.getSingleProspectSequencesDetailsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Mark Single Contact Sequence as Replied
    builder.addCase(
      singleProspectSequencesMarkAsRepliedRequest.pending,
      (state) => {
        state.singleProspectSequencesMarkAsRepliedRequest.status =
          RequestStatus.Pending;
        state.singleProspectSequencesMarkAsRepliedRequest.error = null;
      },
    );
    builder.addCase(
      singleProspectSequencesMarkAsRepliedRequest.fulfilled,
      (state, action) => {
        state.singleProspectSequencesMarkAsRepliedRequest.status =
          RequestStatus.Succeeded;
        state.singleProspectSequencesMarkAsRepliedRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      singleProspectSequencesMarkAsRepliedRequest.rejected,
      (state, action) => {
        state.singleProspectSequencesMarkAsRepliedRequest.status =
          RequestStatus.Failed;
        state.singleProspectSequencesMarkAsRepliedRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Mark Single Contact Sequence as Finished
    builder.addCase(
      singleProspectSequencesMarkAsFinishedRequest.pending,
      (state) => {
        state.singleProspectSequencesMarkAsFinishedRequest.status =
          RequestStatus.Pending;
        state.singleProspectSequencesMarkAsFinishedRequest.error = null;
      },
    );
    builder.addCase(
      singleProspectSequencesMarkAsFinishedRequest.fulfilled,
      (state, action) => {
        state.singleProspectSequencesMarkAsFinishedRequest.status =
          RequestStatus.Succeeded;
        state.singleProspectSequencesMarkAsFinishedRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      singleProspectSequencesMarkAsFinishedRequest.rejected,
      (state, action) => {
        state.singleProspectSequencesMarkAsFinishedRequest.status =
          RequestStatus.Failed;
        state.singleProspectSequencesMarkAsFinishedRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Remove From Sequence for single contact
    builder.addCase(
      singleProspectRemoveFromSequencesRequest.pending,
      (state) => {
        state.singleProspectRemoveFromSequencesRequest.status =
          RequestStatus.Pending;
        state.singleProspectRemoveFromSequencesRequest.error = null;
      },
    );
    builder.addCase(
      singleProspectRemoveFromSequencesRequest.fulfilled,
      (state, action) => {
        state.singleProspectRemoveFromSequencesRequest.status =
          RequestStatus.Succeeded;
        state.singleProspectRemoveFromSequencesRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      singleProspectRemoveFromSequencesRequest.rejected,
      (state, action) => {
        state.singleProspectRemoveFromSequencesRequest.status =
          RequestStatus.Failed;
        state.singleProspectRemoveFromSequencesRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Add Contact to step
    builder.addCase(addToStepRequest.pending, (state) => {
      state.addToStepRequest.status = RequestStatus.Pending;
      state.addToStepRequest.error = null;
    });
    builder.addCase(addToStepRequest.fulfilled, (state, action) => {
      state.addToStepRequest.status = RequestStatus.Succeeded;
      state.addToStepRequest.message = action.payload.message;
      state.addToStepRequestResultCount.failed = action.payload.payload.failed;
      state.addToStepRequestResultCount.successful =
        action.payload.payload.successful;
      state.addToStepRequestResultCount.resultData = action.payload.payload;
    });
    builder.addCase(addToStepRequest.rejected, (state, action) => {
      state.addToStepRequest.status = RequestStatus.Failed;
      state.addToStepRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Save contact attributes
    builder.addCase(saveContactAttributesRequest.pending, (state) => {
      state.saveContactAttributesRequest.status = RequestStatus.Pending;
      state.saveContactAttributesRequest.error = null;
    });
    builder.addCase(saveContactAttributesRequest.fulfilled, (state, action) => {
      state.saveContactAttributesRequest.status = RequestStatus.Succeeded;
      state.saveContactAttributesRequest.message = action.payload.message;
      state.singleProspect = action.payload.payload;
    });
    builder.addCase(saveContactAttributesRequest.rejected, (state, action) => {
      state.saveContactAttributesRequest.status = RequestStatus.Failed;
      state.saveContactAttributesRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // saveContactDealValueRequest
    builder.addCase(saveContactDealValueRequest.pending, (state) => {
      state.saveContactDealValueRequest.status = RequestStatus.Pending;
      state.saveContactDealValueRequest.error = null;
    });
    builder.addCase(saveContactDealValueRequest.fulfilled, (state, action) => {
      state.saveContactDealValueRequest.status = RequestStatus.Succeeded;
      state.saveContactDealValueRequest.message = action.payload.message;
      state.singleProspect = action.payload.payload;
    });
    builder.addCase(saveContactDealValueRequest.rejected, (state, action) => {
      state.saveContactDealValueRequest.status = RequestStatus.Failed;
      state.saveContactDealValueRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Fields
    builder.addCase(getFieldsRequest.pending, (state) => {
      state.getFieldsRequest.status = RequestStatus.Pending;
      state.getFieldsRequest.error = null;
    });
    builder.addCase(getFieldsRequest.fulfilled, (state, action) => {
      state.getFieldsRequest.status = RequestStatus.Succeeded;
      state.getFieldsRequest.message = action.payload.message;
      state.fields = action.payload.payload;
    });
    builder.addCase(getFieldsRequest.rejected, (state, action) => {
      state.getFieldsRequest.status = RequestStatus.Failed;
      state.getFieldsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Add prospect manually
    builder.addCase(addProspectManuallyRequest.pending, (state) => {
      state.addProspectManuallyRequest.status = RequestStatus.Pending;
      state.addProspectManuallyRequest.error = null;
    });
    builder.addCase(addProspectManuallyRequest.fulfilled, (state, action) => {
      state.addProspectManuallyRequest.status = RequestStatus.Succeeded;
      state.addProspectResponse = action.payload.payload;
      state.addProspectManuallyRequest.message = action.payload.message;
    });
    builder.addCase(addProspectManuallyRequest.rejected, (state, action) => {
      state.addProspectManuallyRequest.status = RequestStatus.Failed;
      state.addProspectManuallyRequest.error =
        !action.payload?.isHandled && action.payload;
    });

    // Verify Prospect Manually
    builder.addCase(verifyProspectsManuallyRequest.pending, (state) => {
      state.verifyProspectManuallyRequest.status = RequestStatus.Pending;
      state.verifyProspectManuallyRequest.error = null;
    });
    builder.addCase(
      verifyProspectsManuallyRequest.fulfilled,
      (state, action) => {
        state.verifyProspectManuallyRequest.status = RequestStatus.Succeeded;
        state.verifyProspectManuallyRequest.message = action.payload.message;
      },
    );
    builder.addCase(
      verifyProspectsManuallyRequest.rejected,
      (state, action) => {
        state.verifyProspectManuallyRequest.status = RequestStatus.Failed;
        state.verifyProspectManuallyRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Tags
    builder.addCase(getTagsRequest.pending, (state) => {
      state.getTagsRequest.status = RequestStatus.Pending;
      state.getTagsRequest.error = null;
    });
    builder.addCase(getTagsRequest.fulfilled, (state, action) => {
      state.getTagsRequest.status = RequestStatus.Succeeded;
      state.getTagsRequest.message = action.payload.message;
      state.tags = action.payload.payload;
    });
    builder.addCase(getTagsRequest.rejected, (state, action) => {
      state.getTagsRequest.status = RequestStatus.Failed;
      state.getTagsRequest.error = !action.payload.isHandled && action.payload;
    });

    builder.addCase(getTagsToLeadsRequest.pending, (state) => {
      state.getTagsToLeadsRequest.status = RequestStatus.Pending;
      state.getTagsToLeadsRequest.error = null;
    });
    builder.addCase(getTagsToLeadsRequest.fulfilled, (state, action) => {
      state.getTagsToLeadsRequest.status = RequestStatus.Succeeded;
      state.getTagsToLeadsRequest.message = action.payload.message;
      state.leadTags = action.payload.payload;
    });
    builder.addCase(getTagsToLeadsRequest.rejected, (state, action) => {
      state.getTagsToLeadsRequest.status = RequestStatus.Failed;
      state.getTagsToLeadsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Add Tags to Prospects
    builder.addCase(addTagsToProspectsRequest.pending, (state) => {
      state.addTagsToProspectsRequest.status = RequestStatus.Pending;
      state.addTagsToProspectsRequest.error = null;
    });
    builder.addCase(addTagsToProspectsRequest.fulfilled, (state, action) => {
      state.addTagsToProspectsRequest.status = RequestStatus.Succeeded;
      state.addTagsToProspectsRequest.message = action.payload.message;
    });
    builder.addCase(addTagsToProspectsRequest.rejected, (state, action) => {
      state.addTagsToProspectsRequest.status = RequestStatus.Failed;
      state.addTagsToProspectsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Remove Tags to Prospects
    builder.addCase(removeTagsFromProspectsRequest.pending, (state) => {
      state.removeTagsFromProspectsRequest.status = RequestStatus.Pending;
      state.removeTagsFromProspectsRequest.error = null;
    });
    builder.addCase(
      removeTagsFromProspectsRequest.fulfilled,
      (state, action) => {
        state.removeTagsFromProspectsRequest.status = RequestStatus.Succeeded;
        state.removeTagsFromProspectsRequest.message = action.payload.message;
      },
    );
    builder.addCase(
      removeTagsFromProspectsRequest.rejected,
      (state, action) => {
        state.removeTagsFromProspectsRequest.status = RequestStatus.Failed;
        state.removeTagsFromProspectsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Verify Bulk Prospects
    builder.addCase(verifyBulkProspectsRequest.pending, (state) => {
      state.verifyBulkProspectsRequest.status = RequestStatus.Pending;
      state.verifyBulkProspectsRequest.error = null;
    });
    builder.addCase(verifyBulkProspectsRequest.fulfilled, (state, action) => {
      state.verifyBulkProspectsRequest.status = RequestStatus.Succeeded;
      state.verifyBulkProspectsRequest.message = action.payload.payload.message;
    });
    builder.addCase(verifyBulkProspectsRequest.rejected, (state, action) => {
      state.verifyBulkProspectsRequest.status = RequestStatus.Failed;
      state.verifyBulkProspectsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Delete Bulk Prospects
    builder.addCase(deleteBulkProspectsRequest.pending, (state) => {
      state.deleteBulkProspectsRequest.status = RequestStatus.Pending;
      state.deleteBulkProspectsRequest.error = null;
    });
    builder.addCase(deleteBulkProspectsRequest.fulfilled, (state, action) => {
      state.deleteBulkProspectsRequest.status = RequestStatus.Succeeded;
      state.deleteBulkProspectsRequest.message = action.payload.payload.message;
    });
    builder.addCase(deleteBulkProspectsRequest.rejected, (state, action) => {
      state.deleteBulkProspectsRequest.status = RequestStatus.Failed;
      state.deleteBulkProspectsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Unsubscribe Bulk Prospects
    builder.addCase(unsubscribeBulkProspectRequest.pending, (state) => {
      state.unsubscribeBulkProspectRequest.status = RequestStatus.Pending;
      state.unsubscribeBulkProspectRequest.error = null;
    });
    builder.addCase(
      unsubscribeBulkProspectRequest.fulfilled,
      (state, action) => {
        state.unsubscribeBulkProspectRequest.status = RequestStatus.Succeeded;
        state.unsubscribeBulkProspectRequest.message =
          action.payload.payload.message;
        state.steps = action.payload.payload;
      },
    );
    builder.addCase(
      unsubscribeBulkProspectRequest.rejected,
      (state, action) => {
        state.unsubscribeBulkProspectRequest.status = RequestStatus.Failed;
        state.unsubscribeBulkProspectRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Add Bulk Prospects To Step
    builder.addCase(addBulkProspectsToStepRequest.pending, (state) => {
      state.addBulkProspectsToStepRequest.status = RequestStatus.Pending;
      state.addBulkProspectsToStepRequest.error = null;
    });
    builder.addCase(
      addBulkProspectsToStepRequest.fulfilled,
      (state, action) => {
        state.addBulkProspectsToStepRequest.status = RequestStatus.Succeeded;
        state.addBulkProspectsToStepRequest.message =
          action.payload.payload.message;
        state.addToStepRequestResultCount.failed =
          action.payload.payload.failed;
        state.addToStepRequestResultCount.successful =
          action.payload.payload.successful;
        state.addToStepRequestResultCount.resultData = action.payload.payload;
      },
    );
    builder.addCase(addBulkProspectsToStepRequest.rejected, (state, action) => {
      state.addBulkProspectsToStepRequest.status = RequestStatus.Failed;
      state.addBulkProspectsToStepRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Add Tags To Bulk Prospects
    builder.addCase(addTagsToBulkProspectsRequest.pending, (state) => {
      state.addTagsToBulkProspectsRequest.status = RequestStatus.Pending;
      state.addTagsToBulkProspectsRequest.error = null;
    });
    builder.addCase(
      addTagsToBulkProspectsRequest.fulfilled,
      (state, action) => {
        state.addTagsToBulkProspectsRequest.status = RequestStatus.Succeeded;
        state.addTagsToBulkProspectsRequest.message =
          action.payload.payload.message;
      },
    );
    builder.addCase(addTagsToBulkProspectsRequest.rejected, (state, action) => {
      state.addTagsToBulkProspectsRequest.status = RequestStatus.Failed;
      state.addTagsToBulkProspectsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Remove Tags From Bulk Prospects
    builder.addCase(removeTagsFromBulkProspectsRequest.pending, (state) => {
      state.removeTagsFromBulkProspectsRequest.status = RequestStatus.Pending;
      state.removeTagsFromBulkProspectsRequest.error = null;
    });
    builder.addCase(
      removeTagsFromBulkProspectsRequest.fulfilled,
      (state, action) => {
        state.removeTagsFromBulkProspectsRequest.status =
          RequestStatus.Succeeded;
        state.removeTagsFromBulkProspectsRequest.message =
          action.payload.payload.message;
      },
    );
    builder.addCase(
      removeTagsFromBulkProspectsRequest.rejected,
      (state, action) => {
        state.removeTagsFromBulkProspectsRequest.status = RequestStatus.Failed;
        state.removeTagsFromBulkProspectsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Prospect primary email change
    builder.addCase(changePrimaryEmailInProspectRequest.pending, (state) => {
      state.changePrimaryEmailInProspectRequest.status = RequestStatus.Pending;
      state.changePrimaryEmailInProspectRequest.error = null;
    });
    builder.addCase(
      changePrimaryEmailInProspectRequest.fulfilled,
      (state, action) => {
        state.changePrimaryEmailInProspectRequest.status =
          RequestStatus.Succeeded;
        state.changePrimaryEmailInProspectRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      changePrimaryEmailInProspectRequest.rejected,
      (state, action) => {
        state.changePrimaryEmailInProspectRequest.status = RequestStatus.Failed;
        state.changePrimaryEmailInProspectRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Delete secondary email from prospect
    builder.addCase(deleteSecondaryEmailRequest.pending, (state) => {
      state.deleteSecondaryEmailRequest.status = RequestStatus.Pending;
      state.deleteSecondaryEmailRequest.error = null;
    });
    builder.addCase(deleteSecondaryEmailRequest.fulfilled, (state, action) => {
      state.deleteSecondaryEmailRequest.status = RequestStatus.Succeeded;
      state.deleteSecondaryEmailRequest.message = action.payload.message;
    });
    builder.addCase(deleteSecondaryEmailRequest.rejected, (state, action) => {
      state.deleteSecondaryEmailRequest.status = RequestStatus.Failed;
      state.deleteSecondaryEmailRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Delete secondary Phone from prospect
    builder.addCase(deleteSecondaryPhoneRequest.pending, (state) => {
      state.deleteSecondaryPhoneRequest.status = RequestStatus.Pending;
      state.deleteSecondaryPhoneRequest.error = null;
    });
    builder.addCase(deleteSecondaryPhoneRequest.fulfilled, (state, action) => {
      state.deleteSecondaryPhoneRequest.status = RequestStatus.Succeeded;
      state.deleteSecondaryPhoneRequest.message = action.payload.message;
    });
    builder.addCase(deleteSecondaryPhoneRequest.rejected, (state, action) => {
      state.deleteSecondaryPhoneRequest.status = RequestStatus.Failed;
      state.deleteSecondaryPhoneRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Filter View List
    builder.addCase(getFilterViewListRequest.pending, (state) => {
      state.getFilterViewListRequest.status = RequestStatus.Pending;
      state.getFilterViewListRequest.error = null;
    });
    builder.addCase(getFilterViewListRequest.fulfilled, (state, action) => {
      state.getFilterViewListRequest.status = RequestStatus.Succeeded;
      state.savedFilters = action.payload.payload;
      state.getFilterViewListRequest.message = action.payload.message;
    });
    builder.addCase(getFilterViewListRequest.rejected, (state, action) => {
      state.getFilterViewListRequest.status = RequestStatus.Failed;
      state.getFilterViewListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Pin Unpin Saved Filter
    builder.addCase(pinUnpinSavedFilterRequest.pending, (state) => {
      state.pinUnpinSavedFilterRequest.status = RequestStatus.Pending;
      state.pinUnpinSavedFilterRequest.error = null;
    });
    builder.addCase(pinUnpinSavedFilterRequest.fulfilled, (state, action) => {
      state.pinUnpinSavedFilterRequest.status = RequestStatus.Succeeded;
      state.pinUnpinSavedFilterRequest.message = action.payload.message;

      // Update savedFilters in Redux state without API call
      const { viewId, action: pinUnpinAction } = action.meta.arg;
      const filterIndex = state?.savedFilters?.findIndex(
        (filter) => filter?.id === viewId,
      );

      if (
        filterIndex !== undefined &&
        filterIndex !== -1 &&
        state.savedFilters
      ) {
        if (pinUnpinAction === PinUnpinActionType.Pin) {
          // Set pinnedAt to current timestamp when pinning
          state.savedFilters[filterIndex].pinnedAt = new Date().toISOString();
        } else if (pinUnpinAction === PinUnpinActionType.UnPin) {
          // Clear pinnedAt when unpinning
          state.savedFilters[filterIndex].pinnedAt = null;
        }
      }
    });
    builder.addCase(pinUnpinSavedFilterRequest.rejected, (state, action) => {
      state.pinUnpinSavedFilterRequest.status = RequestStatus.Failed;
      state.pinUnpinSavedFilterRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update Saved Filter
    builder.addCase(updateSavedFilterRequest.pending, (state) => {
      state.updateSavedFilterRequest.status = RequestStatus.Pending;
      state.updateSavedFilterRequest.error = null;
    });
    builder.addCase(updateSavedFilterRequest.fulfilled, (state, action) => {
      state.updateSavedFilterRequest.status = RequestStatus.Succeeded;
      if (
        state.selectedSavedView &&
        state.selectedSavedView?.id === action.payload.payload?.id
      ) {
        state.selectedSavedView = {
          ...(state.selectedSavedView || {}),
          ...(action.payload.payload || {}),
        };
      }
      state.updateSavedFilterRequest.message = action.payload.message;
    });
    builder.addCase(updateSavedFilterRequest.rejected, (state, action) => {
      state.updateSavedFilterRequest.status = RequestStatus.Failed;
      state.updateSavedFilterRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Delete Saved Filter
    builder.addCase(deleteSavedViewRequest.pending, (state) => {
      state.deleteSavedViewRequest.status = RequestStatus.Pending;
      state.deleteSavedViewRequest.error = null;
    });
    builder.addCase(deleteSavedViewRequest.fulfilled, (state, action) => {
      state.deleteSavedViewRequest.status = RequestStatus.Succeeded;
      // state.savedFilters = action.payload.payload;
      state.deleteSavedViewRequest.message = action.payload.message;
    });
    builder.addCase(deleteSavedViewRequest.rejected, (state, action) => {
      state.deleteSavedViewRequest.status = RequestStatus.Failed;
      state.deleteSavedViewRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Contact By View Id
    builder.addCase(getContactListByViewIdRequest.pending, (state) => {
      state.getContactListByViewIdRequest.status = RequestStatus.Pending;
      state.getContactListByViewIdRequest.error = null;
      // state.prospects = [];
      // In Kanban view, when we sort By we are not calling count API, so we should not reset the count
      // state.prospectCount = 0;
      state.prospectKanbanViewData = null;
    });
    builder.addCase(
      getContactListByViewIdRequest.fulfilled,
      (state, action) => {
        state.getContactListByViewIdRequest.status = RequestStatus.Succeeded;

        const {
          payload: { payload },
        } = action;
        const viewType = action.meta.arg?.viewType;

        const contactsPayload = payload.contacts;

        if (viewType === ViewType.Kanban) {
          if (
            contactsPayload &&
            typeof contactsPayload === 'object' &&
            !Array.isArray(contactsPayload)
          ) {
            const { jobIdsInProgress, ...kanbanColumns } = contactsPayload;

            // Check if this is a pagination request for a specific column (activeColumns specified)
            const requestArgs = action.meta.arg || {};
            const { activeColumns } = requestArgs;
            const pageNum = requestArgs.pageNum || 0;

            // If activeColumns is specified and pageNum > 0, merge data instead of replacing
            if (
              activeColumns &&
              Array.isArray(activeColumns) &&
              activeColumns.length > 0 &&
              pageNum > 0
            ) {
              // This is a pagination request for specific columns - merge with existing data
              const existingViewData =
                state.prospectKanbanViewData?.contacts || {};
              const mergedKanbanData = { ...existingViewData };

              // Merge each requested column's data
              activeColumns.forEach((columnKey: string) => {
                const newColumnData = kanbanColumns[columnKey];
                const existingColumnData = existingViewData[columnKey] || [];

                if (Array.isArray(newColumnData) && newColumnData.length > 0) {
                  // Append new prospects to existing column data, avoiding duplicates
                  const existingIds = new Set(
                    existingColumnData.map((p: any) => p.id).filter(Boolean),
                  );
                  const uniqueNewProspects = newColumnData.filter(
                    (p: any) => p.id && !existingIds.has(p.id),
                  );
                  mergedKanbanData[columnKey] = [
                    ...existingColumnData,
                    ...uniqueNewProspects,
                  ];
                } else if (
                  Array.isArray(existingColumnData) &&
                  existingColumnData.length > 0
                ) {
                  // Keep existing data if no new data
                  mergedKanbanData[columnKey] = existingColumnData;
                }
              });

              // Update prospectKanbanViewData with merged data
              state.prospectKanbanViewData = {
                ...(state.prospectKanbanViewData || {}),
                ...payload,
                contacts: {
                  ...(state.prospectKanbanViewData?.contacts || {}),
                  ...mergedKanbanData,
                },
              };
            } else {
              // First page load or full refresh - replace all data
              state.prospectKanbanViewData = payload;
            }

            state.AiColEnrichmentjobIdsInProgress = jobIdsInProgress || [];
          } else {
            state.prospectKanbanViewData = {};
            state.AiColEnrichmentjobIdsInProgress = [];
          }
          // state.prospects = [];
          state.visibleProspectIdsInProspectTable = [];
        } else {
          const results =
            contactsPayload?.results ||
            (Array.isArray(contactsPayload) ? contactsPayload : []);

          state.prospects = results;
          state.AiColEnrichmentjobIdsInProgress =
            contactsPayload?.jobIdsInProgress || [];

          if (results.length > 0) {
            const prospectIds = results
              .map((prospect: any) => prospect.id)
              .filter((id: any) => id !== undefined);

            state.visibleProspectIdsInProspectTable = prospectIds;
          } else {
            state.visibleProspectIdsInProspectTable = [];
          }
        }

        state.selectedSavedView = {
          ...(state.selectedSavedView || {}),
          ...payload?.view,
          ...(state.selectedSavedView?.teams?.length > 0 && {
            teams: state.selectedSavedView?.teams,
          }),
        };
        const { selectedFields, sorting, ...filterGroupLogicNew } =
          payload?.view?.filterCriteria || {};

        const parsedSorting = parseSortingFromResponse(sorting);

        state.filters = {
          ...state.filters,
          pageNum: 1,
          search: '',
          filterGroupLogic: filterGroupLogicNew,
          ...(parsedSorting && {
            sortBy: parsedSorting.sortBy,
            sortOrder: parsedSorting.sortOrder,
          }),
        };
        state.getContactListByViewIdRequest.message = action.payload.message;
      },
    );
    builder.addCase(getContactListByViewIdRequest.rejected, (state, action) => {
      state.getContactListByViewIdRequest.status = RequestStatus.Failed;
      state.getContactListByViewIdRequest.error =
        !action.payload.isHandled && action.payload;
      state.prospectKanbanViewData = null;
    });

    // Get Imported Files List
    builder.addCase(getImportedFilesListRequest.pending, (state) => {
      state.getImportedFilesListRequest.status = RequestStatus.Pending;
      state.getImportedFilesListRequest.error = null;
    });
    builder.addCase(getImportedFilesListRequest.fulfilled, (state, action) => {
      state.getImportedFilesListRequest.status = RequestStatus.Succeeded;
      state.importedFilesList =
        action?.payload?.payload?.length > 0 ? action.payload.payload : [];
    });
    builder.addCase(getImportedFilesListRequest.rejected, (state, action) => {
      state.getImportedFilesListRequest.status = RequestStatus.Failed;
      state.getImportedFilesListRequest.error =
        !action?.payload?.isHandled && action?.payload;
    });
    // Update Prospect field
    builder.addCase(updateProspectFieldByIdRequest.pending, (state) => {
      state.updateProspectFieldByIdRequest.status = RequestStatus.Pending;
      state.updateProspectFieldByIdRequest.error = null;
    });
    builder.addCase(
      updateProspectFieldByIdRequest.fulfilled,
      (state, action) => {
        state.updateProspectFieldByIdRequest.status = RequestStatus.Succeeded;
        state.updateProspectFieldByIdRequest.message = action.payload.message;

        const { fieldRef, attributeValue } = action.meta.arg;

        const updateProspectAttributes = (prospect: any) => {
          if (prospect && prospect.attributes) {
            const attributeIndex = prospect.attributes.findIndex(
              (attr: any) => attr?.fieldRef?.id === fieldRef,
            );
            if (attributeIndex !== -1) {
              prospect.attributes = [
                ...prospect.attributes.slice(0, attributeIndex),
                {
                  ...prospect.attributes[attributeIndex],
                  attributeValue,
                },
                ...prospect.attributes.slice(attributeIndex + 1),
              ];
              return true;
            }
          }
          return false;
        };

        if (state.singleProspect) {
          updateProspectAttributes(state.singleProspect);
          state.singleProspect = { ...state.singleProspect };
        }

        if (Array.isArray(state.getSingleProspect)) {
          if (state.getSingleProspect.length > 0) {
            const updated = updateProspectAttributes(
              state.getSingleProspect[0],
            );
            if (updated) {
              state.getSingleProspect = [...state.getSingleProspect];
            }
          }
        } else if (state.getSingleProspect) {
          const updated = updateProspectAttributes(state.getSingleProspect);
          if (updated) {
            state.getSingleProspect = { ...state.getSingleProspect };
          }
        }
      },
    );
    builder.addCase(
      updateProspectFieldByIdRequest.rejected,
      (state, action) => {
        state.updateProspectFieldByIdRequest.status = RequestStatus.Failed;
        state.updateProspectFieldByIdRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    builder.addCase(
      getLongTextByProspectAndColumnIdRequest.pending,
      (state) => {
        state.getLongTextByProspectAndColumnIdRequest.status =
          RequestStatus.Pending;
        state.getLongTextByProspectAndColumnIdRequest.error = null;
      },
    );
    builder.addCase(
      getLongTextByProspectAndColumnIdRequest.fulfilled,
      (state, action) => {
        state.getLongTextByProspectAndColumnIdRequest.status =
          RequestStatus.Succeeded;
        state.getLongTextByProspectAndColumnIdRequest.message =
          action.payload.message;
        state.longText = action.payload.payload.attributeValue;
      },
    );
    builder.addCase(
      getLongTextByProspectAndColumnIdRequest.rejected,
      (state, action) => {
        state.getLongTextByProspectAndColumnIdRequest.status =
          RequestStatus.Failed;
        state.getLongTextByProspectAndColumnIdRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Ai Template Option listing
    builder.addCase(getTemplateOptionList.pending, (state, action) => {
      state.getAiModelTemplates.status = RequestStatus.Pending;
      state.getAiModelTemplates.error = null;
      // If starting a fresh query (no append), reset list and pagination
      if (!action.meta.arg?.append) {
        // state.aiTemplates = [];
        state.aiTemplatesPagination = {
          currentPage: 1,
          totalItems: 0,
          hasMore: true,
        };
      }
    });
    builder.addCase(getTemplateOptionList.fulfilled, (state, action) => {
      state.getAiModelTemplates.status = RequestStatus.Succeeded;
      state.getAiModelTemplates.message = action?.payload?.message;

      const newTemplates = action.payload.payload.templates;
      const totalItems = action.payload.payload.count;
      // FOR MOCK API
      // const newTemplates = action.payload.data.data;
      // const totalItems = action.payload.data.items;
      const currentPage = action.meta.arg?.page || 1;
      const perPage = action.meta.arg?.perPage || 10;

      if (action.meta.arg?.search || action.meta.arg?.category) {
        state.isAiColumnTemplateListCachedFromFilter = true;
      } else {
        state.isAiColumnTemplateListCachedFromFilter = false;
      }

      // Calculate if there are more pages
      const hasMore = currentPage * perPage < totalItems;

      // If append is true, add to existing templates, otherwise replace
      if (action.meta.arg?.append) {
        state.aiTemplates = [...state.aiTemplates, ...newTemplates];
      } else {
        state.aiTemplates = newTemplates;
      }

      state.aiTemplatesPagination = {
        currentPage,
        totalItems,
        hasMore,
      };
    });
    builder.addCase(getTemplateOptionList.rejected, (state, action) => {
      state.getAiModelTemplates.status = RequestStatus.Failed;
      state.getAiModelTemplates.error =
        !action.payload.isHandled && action.payload;
    });

    // Supported Ai Models
    builder.addCase(getAiModelRequest.pending, (state) => {
      state.getAiModelRequest.status = RequestStatus.Pending;
      state.getAiModelRequest.error = null;
    });
    builder.addCase(getAiModelRequest.fulfilled, (state, action) => {
      state.getAiModelRequest.status = RequestStatus.Succeeded;
      state.getAiModelRequest.message = action.payload.message;
      state.aiModelList = action.payload.payload;
    });
    builder.addCase(getAiModelRequest.rejected, (state, action) => {
      state.getAiModelRequest.status = RequestStatus.Failed;
      state.getAiModelRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Template By Id
    builder.addCase(getTemplateByIdRequest.pending, (state) => {
      state.getTemplateByIdRequest.status = RequestStatus.Pending;
      state.getTemplateByIdRequest.error = null;
    });
    builder.addCase(getTemplateByIdRequest.fulfilled, (state, action) => {
      state.getTemplateByIdRequest.status = RequestStatus.Succeeded;
      state.getTemplateByIdRequest.message = action.payload.message;
      state.getTemplateById = action.payload.payload;
      state.selectedAiTemplateId = action.payload.payload.id;
    });
    builder.addCase(getTemplateByIdRequest.rejected, (state, action) => {
      state.getTemplateByIdRequest.status = RequestStatus.Failed;
      state.getTemplateByIdRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Save and don't run
    builder.addCase(saveAndDontRun.pending, (state, action) => {
      state.saveAndDontRun.status = RequestStatus.Pending;
      state.saveAndDontRun.error = null;
      // Store the payload data for conditional scroll behavior
      state.currentAiColumnPayload = action.meta.arg;
    });
    builder.addCase(saveAndDontRun.fulfilled, (state, action) => {
      state.saveAndDontRun.status = RequestStatus.Succeeded;
      state.saveAndDontRun.message = action.payload.message;
      state.savedNewColumn = action?.payload?.payload || null;
      state.appliedProspectEnrichmentFilters =
        action?.payload?.payload?.appliedProspectEnrichmentFilters || null;
      if (action.payload.payload?.jobId) {
        state.AiColEnrichmentjobIdsInProgress = [
          ...state.AiColEnrichmentjobIdsInProgress,
          action.payload.payload?.jobId,
        ];
      }
    });
    builder.addCase(saveAndDontRun.rejected, (state, action) => {
      state.saveAndDontRun.status = RequestStatus.Failed;
      state.saveAndDontRun.error = !action.payload.isHandled && action.payload;
    });

    // Create AI Column Template
    builder.addCase(createAiColumnTemplateRequest.pending, (state) => {
      state.createAiColumnTemplateRequest.status = RequestStatus.Pending;
      state.createAiColumnTemplateRequest.error = null;
    });
    builder.addCase(
      createAiColumnTemplateRequest.fulfilled,
      (state, action) => {
        state.createAiColumnTemplateRequest.status = RequestStatus.Succeeded;
        state.createAiColumnTemplateRequest.message = action.payload.message;
        state.isAiColumnTemplateListCachedFromFilter = true;
      },
    );
    builder.addCase(createAiColumnTemplateRequest.rejected, (state, action) => {
      state.createAiColumnTemplateRequest.status = RequestStatus.Failed;
      state.createAiColumnTemplateRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update AI Column Template
    builder.addCase(updateAiColumnTemplateRequest.pending, (state) => {
      state.updateAiColumnTemplateRequest.status = RequestStatus.Pending;
      state.updateAiColumnTemplateRequest.error = null;
      state.updateAiColumnTemplateRequest.message = null;
    });
    builder.addCase(
      updateAiColumnTemplateRequest.fulfilled,
      (state, action) => {
        state.updateAiColumnTemplateRequest.status = RequestStatus.Succeeded;
        state.updateAiColumnTemplateRequest.message = action.payload.message;
        state.isAiColumnTemplateListCachedFromFilter = true;
      },
    );
    builder.addCase(updateAiColumnTemplateRequest.rejected, (state, action) => {
      state.updateAiColumnTemplateRequest.status = RequestStatus.Failed;
      state.updateAiColumnTemplateRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Delete AI Column Template
    builder.addCase(deleteAiColumnTemplateRequest.pending, (state) => {
      state.deleteAiColumnTemplateRequest.status = RequestStatus.Pending;
      state.deleteAiColumnTemplateRequest.error = null;
      state.deleteAiColumnTemplateRequest.message = null;
    });
    builder.addCase(
      deleteAiColumnTemplateRequest.fulfilled,
      (state, action) => {
        state.deleteAiColumnTemplateRequest.status = RequestStatus.Succeeded;
        state.deleteAiColumnTemplateRequest.message = action.payload.message;
      },
    );
    builder.addCase(deleteAiColumnTemplateRequest.rejected, (state, action) => {
      state.deleteAiColumnTemplateRequest.status = RequestStatus.Failed;
      state.deleteAiColumnTemplateRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // ai column preview
    builder.addCase(getAiColumnPreviewRequest.pending, (state) => {
      state.getAiColumnPreviewRequest.status = RequestStatus.Pending;
      state.getAiColumnPreviewRequest.error = null;
    });
    builder.addCase(getAiColumnPreviewRequest.fulfilled, (state, action) => {
      state.getAiColumnPreviewRequest.status = RequestStatus.Succeeded;
      state.getAiColumnPreviewRequest.message = action.payload.message;
      state.aiColumnTemplatePreview = action?.payload?.payload || null;
    });
    builder.addCase(getAiColumnPreviewRequest.rejected, (state, action) => {
      state.getAiColumnPreviewRequest.status = RequestStatus.Failed;
      state.getAiColumnPreviewRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // ai column template config
    builder.addCase(getTemplateConfigResponse.pending, (state) => {
      state.getTemplateConfigResponse.status = RequestStatus.Pending;
      state.getTemplateConfigResponse.error = null;
    });
    builder.addCase(getTemplateConfigResponse.fulfilled, (state, action) => {
      state.getTemplateConfigResponse.status = RequestStatus.Succeeded;
      state.getTemplateConfigResponse.message = action.payload.message;
      state.aiColumnTemplateRegenerate = action?.payload?.payload || null;
    });
    builder.addCase(getTemplateConfigResponse.rejected, (state, action) => {
      state.getTemplateConfigResponse.status = RequestStatus.Failed;
      state.getTemplateConfigResponse.error =
        !action.payload.isHandled && action.payload;
    });

    // ai column regenerate tmeplate
    builder.addCase(regenerateAiColumnCellRequest.pending, (state, action) => {
      state.regenerateAiColumnCellRequest.status = RequestStatus.Pending;
      state.regenerateAiColumnCellRequest.error = null;
      // Track the requested cell to toggle loader while Pending
      const { prospectId, fieldId } = action.meta.arg || {};
      if (prospectId && fieldId) {
        const key = `${prospectId}:${fieldId}`;
        if (
          !state.aiColumnloadingCell.some(
            (c) => `${c.prospectId}:${c.fieldId}` === key,
          )
        ) {
          state.aiColumnloadingCell.push({
            prospectId,
            fieldId,
          });
        }
      }
    });
    builder.addCase(
      regenerateAiColumnCellRequest.fulfilled,
      (state, action) => {
        state.regenerateAiColumnCellRequest.status = RequestStatus.Succeeded;
        state.regenerateAiColumnCellRequest.message = action.payload.message;
        state.regeneratedAiColCellResponse = action?.payload?.payload || null;
        // Remove the completed cell from redux state to stop loaidng
        const { prospectId, attribute } = action.payload?.payload || {};
        const fieldId = attribute?.fieldRefId;
        if (prospectId && fieldId) {
          const doneKey = `${prospectId}:${fieldId}`;
          state.aiColumnloadingCell = state.aiColumnloadingCell.filter(
            (c) => `${c.prospectId}:${c.fieldId}` !== doneKey,
          );
        }
      },
    );
    builder.addCase(regenerateAiColumnCellRequest.rejected, (state, action) => {
      state.regenerateAiColumnCellRequest.status = RequestStatus.Failed;
      state.regenerateAiColumnCellRequest.error =
        !action.payload.isHandled && action.payload;
      // Remove the failed cell from redux state to stop loaidng
      const { prospectId, fieldId } = action.meta.arg || {};
      if (prospectId && fieldId) {
        const failKey = `${prospectId}:${fieldId}`;
        state.aiColumnloadingCell = state.aiColumnloadingCell.filter(
          (c) => `${c.prospectId}:${c.fieldId}` !== failKey,
        );
      }
    });

    // ai Coloumn Play Pause Enrichment
    builder.addCase(aiColPlayPauseEnrichmentRequest.pending, (state) => {
      state.aiColPlayPauseEnrichmentRequest.status = RequestStatus.Pending;
      state.aiColPlayPauseEnrichmentRequest.error = null;
    });
    builder.addCase(
      aiColPlayPauseEnrichmentRequest.fulfilled,
      (state, action) => {
        state.aiColPlayPauseEnrichmentRequest.status = RequestStatus.Succeeded;
        state.aiColPlayPauseEnrichmentRequest.message = action.payload.message;
        state.aiColPlayPauseEnrichmentPayload = action.payload.payload;
      },
    );
    builder.addCase(
      aiColPlayPauseEnrichmentRequest.rejected,
      (state, action) => {
        state.aiColPlayPauseEnrichmentRequest.status = RequestStatus.Failed;
        state.aiColPlayPauseEnrichmentRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // AiColumn Active Fields Enrichment Status
    builder.addCase(aiColActiveFieldsEnrichmentStatus.pending, (state) => {
      state.aiColActiveFieldsEnrichmentStatus.status = RequestStatus.Pending;
      state.aiColActiveFieldsEnrichmentStatus.error = null;
    });
    builder.addCase(
      aiColActiveFieldsEnrichmentStatus.fulfilled,
      (state, action) => {
        state.aiColActiveFieldsEnrichmentStatus.status =
          RequestStatus.Succeeded;
        state.aiColActiveFieldsEnrichmentStatus.message =
          action.payload.message;
        state.aiColEnrichedField = action.payload.payload;
      },
    );
    builder.addCase(
      aiColActiveFieldsEnrichmentStatus.rejected,
      (state, action) => {
        state.aiColActiveFieldsEnrichmentStatus.status = RequestStatus.Failed;
        state.aiColActiveFieldsEnrichmentStatus.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Update Primary number
    builder.addCase(updatePrimaryNumberRequest.pending, (state) => {
      state.updatePrimaryNumberRequest.status = RequestStatus.Pending;
      state.updatePrimaryNumberRequest.error = null;
    });
    builder.addCase(updatePrimaryNumberRequest.fulfilled, (state, action) => {
      state.updatePrimaryNumberRequest.status = RequestStatus.Succeeded;
      state.updatePrimaryNumberRequest.message = action.payload.message;
      state.updatePrimaryNumberResponse = action?.payload?.payload;
    });
    builder.addCase(updatePrimaryNumberRequest.rejected, (state, action) => {
      state.updatePrimaryNumberRequest.status = RequestStatus.Failed;
      state.updatePrimaryNumberRequest.message = action.payload.message;
      state.updatePrimaryNumberRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Eligibility Check
    builder.addCase(getEligibilityCheckRequest.pending, (state) => {
      state.getEligibilityCheckRequest.status = RequestStatus.Pending;
      state.getEligibilityCheckRequest.error = null;
    });
    builder.addCase(getEligibilityCheckRequest.fulfilled, (state, action) => {
      state.getEligibilityCheckRequest.status = RequestStatus.Succeeded;
      state.getEligibilityCheckRequest.message = action.payload.message;
      state.eligibilityCheckResponse = action.payload.payload;
    });
    builder.addCase(getEligibilityCheckRequest.rejected, (state, action) => {
      state.getEligibilityCheckRequest.status = RequestStatus.Failed;
      state.getEligibilityCheckRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Kanban List
    builder.addCase(getKanbanListRequest.pending, (state) => {
      state.getKanbanListRequest.status = RequestStatus.Pending;
      state.getKanbanListRequest.error = null;
    });
    builder.addCase(getKanbanListRequest.fulfilled, (state, action) => {
      state.getKanbanListRequest.status = RequestStatus.Succeeded;
      state.kanbanViewList = action.payload.payload;
      state.getKanbanListRequest.message = action.payload.message;
    });
    builder.addCase(getKanbanListRequest.rejected, (state, action) => {
      state.getKanbanListRequest.status = RequestStatus.Failed;
      state.getKanbanListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update Kanban View
    builder.addCase(updateKanbanViewRequest.pending, (state) => {
      state.updateKanbanViewRequest.status = RequestStatus.Pending;
      state.updateKanbanViewRequest.error = null;
    });
    builder.addCase(updateKanbanViewRequest.fulfilled, (state, action) => {
      state.updateKanbanViewRequest.status = RequestStatus.Succeeded;
      state.updateKanbanViewRequest.message = action.payload.message;
      // Optionally update kanbanViewList if the response includes updated data
      if (action?.payload?.payload) {
        // Update the specific kanban view in the list if needed
        const updatedView = action.payload.payload;
        if (updatedView && Array.isArray(state.kanbanViewList)) {
          const index = state.kanbanViewList.findIndex(
            (view) => view.id === action.meta.arg.kanbanViewId,
          );
          if (index !== -1) {
            state.kanbanViewList[index] = {
              ...state.kanbanViewList[index],
              ...updatedView,
            };
          }
        }
      }
    });
    builder.addCase(updateKanbanViewRequest.rejected, (state, action) => {
      state.updateKanbanViewRequest.status = RequestStatus.Failed;
      state.updateKanbanViewRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Create custom field for prospect
    builder.addCase(createCustomFieldRequest.pending, (state) => {
      state.createCustomFieldRequest.status = RequestStatus.Pending;
      state.createCustomFieldRequest.error = null;
      state.createdKanbanViewId = null;
      // state.isLoading = true;
    });
    builder.addCase(createCustomFieldRequest.fulfilled, (state, action) => {
      state.createCustomFieldRequest.status = RequestStatus.Succeeded;
      state.createCustomFieldRequest.message = action.payload.message;
      const kanbanViewIdFromPayload =
        action?.payload?.payload?.kanbanViewId ??
        action?.payload?.data?.kanbanViewId ??
        null;
      state.createdKanbanViewId =
        kanbanViewIdFromPayload !== null &&
        kanbanViewIdFromPayload !== undefined
          ? Number(kanbanViewIdFromPayload)
          : null;
      // state.customField.unshift(action.payload.payload);
      // state.isLoading = false;
    });
    builder.addCase(createCustomFieldRequest.rejected, (state, action) => {
      state.createCustomFieldRequest.status = RequestStatus.Failed;
      state.createCustomFieldRequest.error =
        !action.payload.isHandled && action.payload;
      state.createdKanbanViewId = null;
      // state.isLoading = false;
    });

    // get Prospect Notes List
    builder.addCase(getProspectNotesRequest.pending, (state) => {
      state.getProspectNotesRequest.status = RequestStatus.Pending;
      state.getProspectNotesRequest.error = null;
    });
    builder.addCase(getProspectNotesRequest.fulfilled, (state, action) => {
      state.getProspectNotesRequest.status = RequestStatus.Succeeded;
      state.getProspectNotesRequest.message = action.payload.message;
      state.prospectNotesList = action.payload.payload;
    });
    builder.addCase(getProspectNotesRequest.rejected, (state, action) => {
      state.getProspectNotesRequest.status = RequestStatus.Failed;
      state.getProspectNotesRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // createProspectNotesListRequest
    builder.addCase(createProspectNotesListRequest.pending, (state) => {
      state.createProspectNotesListRequest.status = RequestStatus.Pending;
      state.createProspectNotesListRequest.error = null;
      state.createProspectNoteResponse = null;
    });
    builder.addCase(
      createProspectNotesListRequest.fulfilled,
      (state, action) => {
        state.createProspectNotesListRequest.status = RequestStatus.Succeeded;
        state.createProspectNotesListRequest.message = action.payload.message;
        // Store the draft note from response if available
        state.createProspectNoteResponse = action.payload.payload || null;
      },
    );
    builder.addCase(
      createProspectNotesListRequest.rejected,
      (state, action) => {
        state.createProspectNotesListRequest.status = RequestStatus.Failed;
        state.createProspectNotesListRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Delete Prospect Note
    builder.addCase(deleteProspectNoteRequest.pending, (state) => {
      state.deleteProspectNoteRequest.status = RequestStatus.Pending;
      state.deleteProspectNoteRequest.error = null;
    });
    builder.addCase(deleteProspectNoteRequest.fulfilled, (state, action) => {
      state.deleteProspectNoteRequest.status = RequestStatus.Succeeded;
      state.deleteProspectNoteRequest.message = action.payload.message;
    });
    builder.addCase(deleteProspectNoteRequest.rejected, (state, action) => {
      state.deleteProspectNoteRequest.status = RequestStatus.Failed;
      state.deleteProspectNoteRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Prospect Note Pin
    builder.addCase(prospectNotePinRequest.pending, (state) => {
      state.prospectNotePinRequest.status = RequestStatus.Pending;
      state.prospectNotePinRequest.error = null;
    });
    builder.addCase(prospectNotePinRequest.fulfilled, (state, action) => {
      state.prospectNotePinRequest.status = RequestStatus.Succeeded;
      state.prospectNotePinRequest.message = action.payload.message;
    });
    builder.addCase(prospectNotePinRequest.rejected, (state, action) => {
      state.prospectNotePinRequest.status = RequestStatus.Failed;
      state.prospectNotePinRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update Prospect Note
    builder.addCase(updateProspectNoteRequest.pending, (state) => {
      state.updateProspectNoteRequest.status = RequestStatus.Pending;
      state.updateProspectNoteRequest.error = null;
    });
    builder.addCase(updateProspectNoteRequest.fulfilled, (state, action) => {
      state.updateProspectNoteRequest.status = RequestStatus.Succeeded;
      state.updateProspectNoteRequest.message = action.payload.message;
      state.updateProspectNoteResponse = action.payload.payload || null;
    });
    builder.addCase(updateProspectNoteRequest.rejected, (state, action) => {
      state.updateProspectNoteRequest.status = RequestStatus.Failed;
      state.updateProspectNoteRequest.error =
        !action.payload.isHandled && action.payload;
      state.updateProspectNoteResponse = null;
    });

    // Update Prospect Note Visibility
    builder.addCase(updateProspectNoteVisibilityRequest.pending, (state) => {
      state.updateProspectNoteVisibilityRequest.status = RequestStatus.Pending;
      state.updateProspectNoteVisibilityRequest.error = null;
    });
    builder.addCase(
      updateProspectNoteVisibilityRequest.fulfilled,
      (state, action) => {
        state.updateProspectNoteVisibilityRequest.status =
          RequestStatus.Succeeded;
        state.updateProspectNoteVisibilityRequest.message =
          action.payload.message;
        state.updateProspectNoteVisibilityResponse =
          action.payload.payload || null;
      },
    );
    builder.addCase(
      updateProspectNoteVisibilityRequest.rejected,
      (state, action) => {
        state.updateProspectNoteVisibilityRequest.status = RequestStatus.Failed;
        state.updateProspectNoteVisibilityRequest.error =
          !action.payload.isHandled && action.payload;
        state.updateProspectNoteVisibilityResponse = null;
      },
    );

    // Get Single Propsect Detail Pinned Fields Request
    builder.addCase(
      getSinglePropsectDetailPinnedFieldsRequest.pending,
      (state) => {
        state.getSinglePropsectDetailPinnedFieldsRequest.status =
          RequestStatus.Pending;
        state.getSinglePropsectDetailPinnedFieldsRequest.error = null;
      },
    );
    builder.addCase(
      getSinglePropsectDetailPinnedFieldsRequest.fulfilled,
      (state, action) => {
        state.getSinglePropsectDetailPinnedFieldsRequest.status =
          RequestStatus.Succeeded;
        state.getSinglePropsectDetailPinnedFieldsRequest.message =
          action.payload.message;
        state.getSingleProspectPinnedFields = action.payload?.payload;
      },
    );
    builder.addCase(
      getSinglePropsectDetailPinnedFieldsRequest.rejected,
      (state, action) => {
        state.getSinglePropsectDetailPinnedFieldsRequest.status =
          RequestStatus.Failed;
        state.getSinglePropsectDetailPinnedFieldsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Update Prospect Pin Status Request
    builder.addCase(updateProspectPinStatusRequest.pending, (state) => {
      state.updateProspectPinStatusRequest.status = RequestStatus.Pending;
      state.updateProspectPinStatusRequest.error = null;
    });
    builder.addCase(
      updateProspectPinStatusRequest.fulfilled,
      (state, action) => {
        state.updateProspectPinStatusRequest.status = RequestStatus.Succeeded;
        state.updateProspectPinStatusRequest.message = action.payload.message;
      },
    );
    builder.addCase(
      updateProspectPinStatusRequest.rejected,
      (state, action) => {
        state.updateProspectPinStatusRequest.status = RequestStatus.Failed;
        state.updateProspectPinStatusRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // get Minimal Email Account List Request
    builder.addCase(getMinimalEmailAccountListRequest.pending, (state) => {
      state.getMinimalEmailAccountListRequest.status = RequestStatus.Pending;
      state.getMinimalEmailAccountListRequest.error = null;
    });
    builder.addCase(
      getMinimalEmailAccountListRequest.fulfilled,
      (state, action) => {
        state.getMinimalEmailAccountListRequest.status =
          RequestStatus.Succeeded;
        state.getMinimalEmailAccountListRequest.message =
          action.payload.message;
        state.getEmailAccountList = action?.payload?.payload;
      },
    );
    builder.addCase(
      getMinimalEmailAccountListRequest.rejected,
      (state, action) => {
        state.getMinimalEmailAccountListRequest.status = RequestStatus.Failed;
        state.getMinimalEmailAccountListRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Update outcome locally when outcome change request succeeds
    builder.addCase(onOutcomeChangeRequest.fulfilled, (state, action) => {
      const { sequenceId, outcomeId } = action.meta.arg;

      const updateSequenceProspects = (prospect) => {
        if (!prospect?.sequenceProspects) return prospect;

        return {
          ...prospect,
          sequenceProspects: prospect.sequenceProspects.map((item) => {
            const currentStep = item?.currentStep || item;
            const sequenceProspect = item?.sequenceProspect || item;
            const itemSequenceId =
              currentStep?.sequence?.id ?? item?.sequence?.id;

            // Update categoryId if this is the sequence that was changed
            if (itemSequenceId === sequenceId) {
              return {
                ...item,
                sequenceProspect: sequenceProspect
                  ? {
                      ...sequenceProspect,
                      categoryId: outcomeId,
                    }
                  : undefined,
                categoryId: outcomeId,
              };
            }
            return item;
          }),
        };
      };

      // Update both singleProspect and getSingleProspect
      if (state.getSingleProspect) {
        state.getSingleProspect = updateSequenceProspects(
          state.getSingleProspect,
        );
      }
      if (state.singleProspect) {
        state.singleProspect = updateSequenceProspects(state.singleProspect);
      }
    });

    // Get Prospect Activity Count
    builder.addCase(getProspectActivityCountRequest.pending, (state) => {
      state.getProspectActivityCountRequest.status = RequestStatus.Pending;
      state.prospectActivityCount = initialState.prospectActivityCount;
      state.getProspectActivityCountRequest.error = null;
    });
    builder.addCase(
      getProspectActivityCountRequest.fulfilled,
      (state, action) => {
        state.getProspectActivityCountRequest.status = RequestStatus.Succeeded;
        state.getProspectActivityCountRequest.message = action.payload?.message;
        state.prospectActivityCount =
          action.payload?.payload || initialState.prospectActivityCount;
      },
    );
    builder.addCase(
      getProspectActivityCountRequest.rejected,
      (state, action) => {
        state.getProspectActivityCountRequest.status = RequestStatus.Failed;
        state.prospectActivityCount = initialState.prospectActivityCount;
        state.getProspectActivityCountRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
  },
});

export const {
  resetGetProspectListRequest,
  setFilters,
  resetProspectFiltersSelectedFields,
  resetGetProspectFiltersFieldsListRequest,
  resetSaveProspectFiltersViewRequest,
  resetUpdateProspectFiltersViewRequest,
  resetPinUnpinSavedFilterRequest,
  resetUpdateSavedFilterRequest,
  resetDeleteSavedViewRequest,
  resetGetContactListByViewIdRequest,

  changeProspectPagination,
  resetProspect,
  saveSearch,
  resetSequenceAndStepData,
  selectAllContacts,
  clearSelection,
  checkAll,
  checkSingle,
  resetStep,
  resetSelectedContacts,
  setProspectDetails,
  resetAddToProspectForm,
  setProspectsFilters,
  resetProspectsFilters,
  resetTags,
  resetRemoveProspectRequest,
  resetBulkRequests,
  resetUnsubscribeRequestState,
  resetSaveContactAttributesRequestStatus,
  resetProspectDeleteEmailRequestStatus,
  resetProspectDeletePhoneRequestStatus,
  resetProspectPrimaryEmailChangeRequestStatus,
  setSelectedSavedView,
  removedSelectedSavedView,
  resetFilterGroupLogic,
  resetProspectFiltersFieldsAttributesValue,
  resetGetStepDetailsListRequest,
  resetGetProspectListByIdsRequest,
  resetGetLongTextByProspectAndColumnIdRequest,
  resetSingleProspect,
  setSelectedAiTemplateId,
  setSelectedAiTemplateShareableId,
  removeSelectedField,
  resetSaveAndDontRunRequest,
  resetCreateAiColumnTemplateRequest,
  resetUpdateAiColumnTemplateRequest,
  resetDeleteAiColumnTemplateRequest,
  resetAiColumnTemplatePreview,
  resetSingleProspectDetailsRequest,
  setSelectedProspectRowId,
  resetSelectedProspectRowId,
  resetGetTemplateById,
  resetgetTemplateByIdRequest,
  resetAiColumnTemplateRegenerate,
  resetRegenerateAiColumnCellRequest,
  resetGetEligibilityCheckRequest,
  resetRegeneratedTemplateResponse,
  setIsAiColumnLoading,
  resetCurrentAiColumnPayload,
  setInProgressTableCells,
  resetInProgressTableCells,
  setDeSelectedProspectRowId,
  resetDeSelectedProspectRowId,
  setIsBulkProspectSelected,
  resetIsBulkProspectSelected,
  resetAiColPlayPauseEnrichmentRequest,
  resetGetTemplateConfigResponse,
  setAiColumnModalVisible,
  setIsAiColumnTemplateListCachedFromFilter,
  setAiEnrichmentOpenContext,
  resetAiEnrichmentOpenContext,
  setVisibleFieldIds,
  resetVisibleFieldIds,
  setAiColumnStoppedFieldIds,
  setShowAiColumnWelcomeScreen,
  resetAiColumnPreviewRequest,
  resetUpdateProspectFieldByIdRequest,
  resetSavedNewColumn,
  resetUpdatePrimaryNumberState,
  setAutoRunConditionFilterGroups,
  setAutoRunConditionFilterGroupsLogic,
  resetAutoRunConditionFilterGroups,
  resetAppliedProspectEnrichmentFilters,
  setIsAiColEnrichmentFromModal,
  setEnrichmentCompletedProspectIdsFromFirebase,
  setVisibleProspectIdsInProspectTable,
  setContactIdsAndFieldIdsToRefresh,
  setSelectedKanbanCategoryId,
  resetUpdateKanbanViewRequest,
  setHasAppliedKanbanSelectedFields,
  resetHasAppliedKanbanSelectedFields,
  resetCreateCustomFieldRequest,
  setActiveViewId,
  setCurrentViewType,
  resetCreateProspectNotesListRequest,
  resetDeleteProspectNoteRequest,
  resetProspectNotePinRequest,
  resetUpdateProspectNoteRequest,
  resetGetProspectNotesRequest,
  resetUpdateProspectNoteVisibilityRequest,
  setSelectedKanbanCardsFields,
  setKanbanViewList,
  resetKanbanViewList,
  setProspectKanbanCount,
  setUpdatedCategoryResponse,
  setProspectToNavigateFromModal,
  appendProspectIdsToNavigateFromModal,
  setSelectedCategoryName,
  setShowProspectDetailModal,
  setShouldFetchInitialList,
  setAddClickedFieldId,
  setEmailFieldHovered,
  setProspectViewIdFilter,
  setProspectActivityCount,
  resetLoadedSkeletonForCurrrentProspect,
  setLoadedSkeletonForCurrrentProspect,
  resetGetSingleProspectSequencesDetailsRequest,
  setAddNewCategoryTriggered,
} = prospectSlice.actions;

export default prospectSlice.reducer;
